﻿import React, { useState, useEffect, useRef, useCallback, memo} from 'react';
import { apiFetch } from '../services/apiClient';
import { motion, AnimatePresence } from 'motion/react';
import { Send, Loader2, ArrowLeft, Ghost, Zap, Cpu, Sparkles, FileText, Mic, CheckCircle2, Pause, Play, Square, ChevronDown, ChevronRight, XCircle, Copy, Check, Paperclip, Image as ImageIcon, Download, MessageCircle, Briefcase, User, Plus, ListTodo, Circle, Loader, CircleDot, Volume2, VolumeX, Pencil, RefreshCw, ThumbsUp, ThumbsDown, Terminal, FolderOpen, Tag as TagIcon, AlertCircle } from 'lucide-react';
import { ChatInputToolbar, type ChatStrategy, type TeamMode } from './ChatInputToolbar';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import mermaid from 'mermaid';
// 🔴 2026-07-17 v3.6.60: mermaid.initialize 必须移到独立模块（不能在两个 import 中间调用）
import { initMermaidOnce } from '@/lib/mermaid';
initMermaidOnce();
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useTTS } from '@/hooks/useTTS';
import { GlassPanelCard, PulseCounter } from './SharedUI';
import { APP_VERSION } from '@/lib/version';
import { toast } from 'sonner';
import { FoundersSanctuary } from './FoundersSanctuary';
import * as conversationService from '@/services/conversationService';
import { usePlatform } from '@/hooks/usePlatform';
import { runAgentLogic, AgentResponse } from '@/services/agentService';
import { useApp } from '@/contexts/AppContext';
import { VoiceCallButton } from './VoiceCallButton';
import { socketService } from '@/services/socketService';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { useVoiceCloning } from '@/hooks/useVoiceCloning';
import { listVoices } from '@/services/voiceService';
import WorkflowPanel, { type BackgroundWorkflowTask, type WorkflowStep } from './WorkflowPanel';
import { WeChatSettings } from './WeChatSettings';
import { voiceDisplayName } from './VoicePicker';
import * as projectService from '@/services/projectService';
import { EnhancedCodeBlock } from './ui/EnhancedCodeBlock';
import { useTheme } from '@/contexts/ThemeProvider';
import { classifyIntent, type IntentClassification } from '../services/intentClassifier';
import { CHAT_CONFIG } from '@/lib/config';
const CHAT_HISTORY_LIMIT = CHAT_CONFIG.HISTORY_LIMIT;
const CHAT_RENDER_LIMIT = CHAT_CONFIG.RENDER_LIMIT;
const CHAT_PAGE_SIZE = CHAT_CONFIG.PAGE_SIZE;
const CHAT_SEARCH_LIMIT = CHAT_CONFIG.SEARCH_LIMIT;
type WorkflowStatus = 'idle' | 'thinking' | 'background' | 'executing' | 'waiting_confirmation' | 'done' | 'error';

type ChatAttachment = {
  id: string;
  fileName: string;
  path?: string;
  content?: string | null;
  preview?: string | null;
  mimeType?: string;
  size?: number;
  kind: 'image' | 'file';
  downloadUrl?: string;
};

type GeneratedFileLink = {
  id: string;
  fileName: string;
  path: string;
  url: string;
  kind: 'image' | 'document' | 'deck' | 'sheet' | 'pdf' | 'cad' | 'file';
};

// 人格头像映射 — 与 personality 系统融合（personalityId → 显示字 + 黑金渐变）
const PERSONALITY_AVATARS: Record<string, { label: string; gradient: string }> = {
  xiaofeng: { label: '枫', gradient: 'from-amber-400 via-yellow-500 to-amber-600' },
};

// 消息头像：AI 用人格标识（黑金渐变），用户用名称首字
function MessageAvatar({ isAgent, userName, personalityId }: { isAgent: boolean; userName?: string; personalityId?: string }) {
  if (isAgent) {
    const meta = PERSONALITY_AVATARS[personalityId || 'xiaofeng'] || PERSONALITY_AVATARS.xiaofeng;
    return (
      <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-black font-black text-sm shadow-lg shadow-amber-500/25 border border-amber-300/30`}>
        {meta.label}
      </div>
    );
  }
  const initial = (userName || '').trim().charAt(0).toUpperCase() || '用';
  return (
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/75 font-bold text-sm">
      {initial}
    </div>
  );
}

function getDisplayText(message: any): string {
  if (typeof message?.text === 'string') return message.text;
  if (message?.text == null) return '';
  return String(message.text);
}

function buildChatHistoryPayload(messages: any[]) {
  return messages.flatMap((m) => {
    const text = getDisplayText(m).trim();
    const attachmentSummary = Array.isArray(m.attachments) && m.attachments.length > 0
      ? `\n\n[Attachments]\n${m.attachments.map((item: ChatAttachment) => `- ${item.fileName}${item.kind === 'image' ? ' (image)' : ''}`).join('\n')}`
      : '';
    if (!text && !attachmentSummary) return [];
    if (m.type === 'tool') return [];
    if (['error', 'proactive'].includes(m.source)) return [];
    if (/^(Request failed|请求失败|出错了|Failed to route)/i.test(text)) return [];
    if (m.type === 'agent') return [{ role: 'assistant', content: text }];
    if (m.type === 'user' || m.type === 'file_context') return [{ role: 'user', content: `${text}${attachmentSummary}`.trim() }];
    return [];
  }).slice(-80);
}

function isImageFileName(name: string, mimeType?: string): boolean {
  return Boolean(mimeType?.startsWith('image/')) || /\.(png|jpe?g|webp|bmp|gif|tiff?)$/i.test(name || '');
}

const CHAT_ATTACHMENT_ACCEPT = [
  '.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff',
  '.txt,.md,.json,.csv,.pdf,.docx,.xlsx,.xls,.pptx,.ppt,.rtf,.ts,.tsx,.js,.jsx,.py,.html,.css,.yaml,.yml,.xml,.log',
].join(',');

const GENERATED_FILE_EXTS = 'docx|pptx|xlsx|xls|pdf|txt|md|csv|json|png|jpe?g|webp|gif|svg|html|dxf|dwg';
const WINDOWS_GENERATED_FILE_RE = new RegExp(`[A-Za-z]:\\\\[^\\n\\r"'<>|]+?\\.(?:${GENERATED_FILE_EXTS})\\b`, 'gi');
const XF_OUTPUT_FILE_RE = new RegExp(`/xf_output/[^\\s\\])"'<>]+?\\.(?:${GENERATED_FILE_EXTS})\\b`, 'gi');

function generatedFileKind(fileName: string): GeneratedFileLink['kind'] {
  const lower = fileName.toLowerCase();
  if (/\.(png|jpe?g|webp|gif|svg)$/i.test(lower)) return 'image';
  if (/\.pptx?$/i.test(lower)) return 'deck';
  if (/\.xlsx?$/i.test(lower)) return 'sheet';
  if (/\.pdf$/i.test(lower)) return 'pdf';
  if (/\.(dxf|dwg)$/i.test(lower)) return 'cad';
  if (/\.(docx?|txt|md|csv|json|html)$/i.test(lower)) return 'document';
  return 'file';
}

function buildGeneratedFileUrl(filePath: string): string {
  if (filePath.startsWith('/xf_output/')) return filePath;
  return `/api/files/generated?path=${encodeURIComponent(filePath)}`;
}

function extractGeneratedFiles(text: string): GeneratedFileLink[] {
  const seen = new Set<string>();
  const candidates = [
    ...(text.match(WINDOWS_GENERATED_FILE_RE) || []),
    ...(text.match(XF_OUTPUT_FILE_RE) || []),
  ];

  return candidates
    .map(raw => raw.trim().replace(/[)\].,;，。；]+$/g, ''))
    .filter(filePath => {
      const key = filePath.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(filePath => {
      const fileName = filePath.split(/[\\/]/).pop() || filePath;
      return {
        id: `generated-${filePath}`,
        fileName,
        path: filePath,
        url: buildGeneratedFileUrl(filePath),
        kind: generatedFileKind(fileName),
      };
    });
}


type SlashCommand = {
  id: string;
  label: string;
  desc: string;
  icon: string;
  action: 'clear' | 'export' | 'help' | 'image' | 'search' | 'insert';
  text?: string;
};

const SLASH_COMMANDS: SlashCommand[] = [
  { id: 'clear', label: '清空对话', desc: '清除当前所有消息', icon: 'Clear', action: 'clear' },
  { id: 'export', label: '导出对话', desc: '导出为 Markdown 文件', icon: 'Download', action: 'export' },
  { id: 'help', label: '帮助', desc: '显示可用命令和快捷键', icon: 'Help', action: 'help', text: '可用命令: /clear 清空 /export 导出 /help 帮助 /image 生成图片 /search 搜索。快捷键: Ctrl+Enter 发送, Escape 关闭面板, Ctrl+V 粘贴图片, 拖拽文件上传。' },
  { id: 'image', label: '生成图片', desc: '用 AI 生成图片', icon: 'Image', action: 'insert', text: '请帮我生成一张图片: ' },
  { id: 'search', label: '搜索记忆', desc: '搜索知识库和记忆', icon: 'Search', action: 'insert', text: '请帮我在知识库中搜索: ' },
  { id: 'export-json', label: '导出JSON', desc: '导出对话为 JSON 文件', icon: 'Download', action: 'exportJson' },
  { id: 'export-pdf', label: '导出PDF', desc: '导出对话为 PDF 文件', icon: 'Download', action: 'exportPdf' },
];
function AgentChatPageImpl({ t, user, agent, isOpen, onClose, prefillMessage, onPrefillConsumed, conversationId, onConversationChange, embedded }: {
  t: any; user: any; agent?: any; isOpen: boolean; onClose: () => void;
  prefillMessage?: string; onPrefillConsumed?: () => void;
  conversationId?: string | null;  // NEW: explicit conversation ID to load
  onConversationChange?: (conversationId: string | null) => void;  // NEW: notify parent of changes
  embedded?: boolean;  // NEW: render as a flex child (sidebar + chat layout) instead of fullscreen overlay
}) {
  const [messages, setMessages] = useState<any[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [agentMetadata, setAgentMetadata] = useState<Partial<AgentResponse>>({});
  const [engineVersion, setEngineVersion] = useState<string>(APP_VERSION);
  const [syncSpeedMs, setSyncSpeedMs] = useState<number>(0);
  const [earlierOffset, setEarlierOffset] = useState(0);
  const [draftText, setDraftTextState] = useState<string>("");
  const [livePlan, setLivePlan] = useState<any | null>(null);
  const [convProjectId, setConvProjectId] = useState<string | undefined>(undefined);
  const [projProjects, setProjProjects] = useState<projectService.Project[]>([]);
  const [showProjMenu, setShowProjMenu] = useState(false);

  // EMA smoothing for sync speed display (alpha=0.3, smooths out spikes)
  const emaRef = useRef<number>(0);
  const formatSyncSpeed = (raw: number): number => {
    // Filter outliers: >30s is likely a timeout/hang, ignore
    if (raw <= 0 || raw > 30000) return emaRef.current;
    // EMA smoothing
    if (emaRef.current === 0) { emaRef.current = raw; return raw; }
    emaRef.current = Math.round(emaRef.current * 0.7 + raw * 0.3);
    return emaRef.current;
  };
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => isZh ? zh : en;
  const { platform, isElectron } = usePlatform();
  const { aiConfig, orgConnection, workDomain, autoSpeakReply, setAutoSpeakReply, selectedVoiceId, setSelectedVoiceId: setCtxSelectedVoiceId } = useApp();
  const isWorkChat = workDomain === 'work' && Boolean(orgConnection?.connected && orgConnection?.orgId);
  const activeDomain = isWorkChat ? 'work' : 'personal';
  const activeOrgId = isWorkChat ? orgConnection?.orgId : undefined;
  const activeDomainLabel = isWorkChat ? ui('公司域 小枫', 'Company 小枫') : ui('个人 小枫', 'Personal 小枫');
  const activeDomainDetail = isWorkChat
    ? ui('当前消息、附件、记忆和工具调用进入组织工作域。', 'Messages, attachments, memories, and tools are scoped to the organization.')
    : ui('当前消息只进入个人域，不写入组织知识和组织记忆。', 'Messages stay in your personal domain and do not write to organization knowledge or memory.');
  const socket = socketService.connect();
  const [voices, setVoices] = useState<any[]>([]);
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const [showWeChatSettings, setShowWeChatSettings] = useState(false);
  const [intentData, setIntentData] = useState<IntentClassification | null>(null);

  const voicePickerRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [msgSelectMode, setMsgSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState<Set<string>>(new Set());
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState<string | null>(null);
  const [feedbackMap, setFeedbackMap] = useState<Record<string, 'like' | 'dislike' | null>>({});
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [installedSkillNames, setInstalledSkillNames] = useState<string[]>([]);
  const [mcpConnectorNames, setMcpConnectorNames] = useState<string[]>([]);
  const [teamMode, setTeamMode] = useState<TeamMode>(() => {
    try { return (localStorage.getItem('xf_team_mode') as TeamMode) || 'solo'; } catch { return 'solo'; }
  });
  const [chatStrategy, setChatStrategy] = useState<ChatStrategy>(() => {
    try { return (localStorage.getItem('xf_chat_strategy') as ChatStrategy) || 'plan'; } catch { return 'plan'; }
  });

  // Fetch installed skills to generate dynamic suggestions
  useEffect(() => {
    apiFetch('/api/skills').then(r => r.json()).then(data => {
      setInstalledSkillNames((data.skills || []).map((s: any) => s.name?.toLowerCase?.() || ''));
    }).catch(() => {});
  }, []);

  // Fetch MCP connectors
  useEffect(() => {
    apiFetch('/api/mcp').then(r => r.json()).then(data => {
      const connectors = (data || []).filter((c: any) => c.status === 'connected' || c.enabled === true);
      setMcpConnectorNames(connectors.map((c: any) => c.name || c.id || 'unknown'));
    }).catch(() => {});
  }, []);

  // Persist chat strategy
  useEffect(() => {
    localStorage.setItem('xf_chat_strategy', chatStrategy);
  }, [chatStrategy]);

  // P0: live task list — pull the most recent active plan and subscribe to socket updates
  const fetchLatestPlan = useCallback(async () => {
    try {
      const res = await apiFetch('/api/plans?status=active&source=xiaofeng&limit=1');
      const data = await res.json();
      const plans = data?.plans || [];
      setLivePlan(plans.length > 0 ? plans[0] : null);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => {
    fetchLatestPlan();
    const sk = socketService.getSocket();
    const onPlan = (payload: any) => {
      if (payload?.plan) setLivePlan(payload.plan);
    };
    sk?.on('plan:updated', onPlan);
    return () => { sk?.off('plan:updated', onPlan); };
  }, [fetchLatestPlan]);

  // P1.5: current conversation's project indicator
  const refreshConvProject = useCallback(async (cid: string | null) => {
    if (!cid) { setConvProjectId(undefined); return; }
    try {
      const res = await apiFetch(`/api/conversations/${cid}`);
      if (res.ok) {
        const data = await res.json();
        setConvProjectId(data?.conversation?.projectId || undefined);
      }
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { refreshConvProject(activeConversationId); }, [activeConversationId, refreshConvProject]);
  useEffect(() => {
    projectService.listProjects().then(setProjProjects).catch(() => {});
  }, []);
  const currentProject = projProjects.find((p) => p.id === convProjectId) || null;
  const handleAssignConvProject = useCallback(async (pid: string | null) => {
    if (!activeConversationId) return;
    try {
      await projectService.bindConversationToProject(activeConversationId, pid);
      setConvProjectId(pid || undefined);
      setShowProjMenu(false);
      window.dispatchEvent(new Event('xf:refresh-conversations'));
    } catch { /* non-fatal */ }
  }, [activeConversationId]);

  // P-7: Stable project select handler using data attributes
  const handleProjectSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const pid = e.currentTarget.dataset.projectId || null;
    handleAssignConvProject(pid);
  }, [handleAssignConvProject]);



  // Fetch real-time engine info and latency stats
  useEffect(() => {
    let cancelled = false;
    const fetchStats = () => {
      if (cancelled) return;
      // Engine version
      apiFetch('/api/engine/info')
        .then(r => r.json())
        .then(data => {
          // data.version already includes "v" prefix (e.g. "v3.1.1")
          if (!cancelled && data?.version) setEngineVersion(data.version);
        })
        .catch(() => {});
      // Sync speed (latency) — with EMA smoothing
      apiFetch('/api/monitor/latency')
        .then(r => r.json())
        .then(data => {
          if (!cancelled && data?.llm?.avgMs) {
            setSyncSpeedMs(formatSyncSpeed(data.llm.avgMs));
          }
        })
        .catch(() => {});
    };
    fetchStats();
    const interval = setInterval(fetchStats, 10000); // Refresh every 10s
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const hasCreativeSkill = installedSkillNames.some((n: string) => ['minimax', 'pixelle', 'video-editor', 'video editor'].some(k => n.includes(k)));
  const hasFetcher = installedSkillNames.some((n: string) => ['fetcher', 'web'].some(k => n.includes(k)));
  const hasDesktop = installedSkillNames.some((n: string) => ['desktop', 'commander'].some(k => n.includes(k)));

  const quickSuggestions = [
    { id: 'chat', label: t.suggestChat || ui('随便聊聊', 'Just Chat'), prompt: ui('你好 小枫，今天有什么有趣的发现吗？', 'Hi 小枫, any interesting discoveries today?'), show: true },
    { id: 'write', label: t.suggestWrite || ui('写作助手', 'Write'), prompt: ui('帮我写一份结构清晰的周报大纲', 'Draft a clear weekly report outline'), show: true },
    { id: 'summarize', label: t.suggestSummarize || ui('总结要点', 'Summarize'), prompt: ui('帮我把这段文字提炼成三个核心要点', 'Distill this text into three key points'), show: true },
    { id: 'brainstorm', label: t.suggestBrainstorm || ui('头脑风暴', 'Brainstorm'), prompt: ui('给我五个关于智能家居产品的创意点子', 'Give me five creative ideas for smart home products'), show: true },
    { id: 'creative', label: t.suggestCreative || ui('生成一张图片', 'Generate Image'), prompt: ui('帮我生成一张星空下的赛博朋克城市图片', 'Generate an image of a cyberpunk city under a starry sky'), show: hasCreativeSkill },
    { id: 'fetch', label: t.suggestFetch || ui('总结网页内容', 'Summarize Webpage'), prompt: ui('帮我抓取这篇文章的内容并总结要点', 'Fetch this article and summarize the key points'), show: hasFetcher },
    { id: 'desktop', label: t.suggestDesktop || ui('桌面整理', 'Organize Desktop'), prompt: ui('帮我把桌面上的文件按日期整理一下', 'Organize the desktop files by date'), show: hasDesktop },
    { id: 'music', label: t.suggestMusic || ui('创作一首音乐', 'Create Music'), prompt: ui('帮我创作一首舒缓的钢琴曲，带有海浪的声音', 'Create a calm piano track with ocean wave ambience'), show: hasCreativeSkill },
  ];

  const visibleSuggestions = quickSuggestions.filter(s => s.show).slice(0, 4);

  const { callState, audioLevel, startCall, endCall, error: callError } = useVoiceCall({
    socket,
    onTranscript: (text, isFinal) => {
      if (isFinal) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text,
          userName: user?.displayName || user?.username || 'You',
          timestamp: new Date().toISOString(),
          type: 'user',
          source: 'voice',
        }]);
      }
    },
  });

  useEffect(() => {
    listVoices().then(data => {
      const all = [...data.cloned, ...data.premade];
      setVoices(all);
      if (all.length > 0 && !selectedVoiceId) {
        setCtxSelectedVoiceId(all[0].voiceId, all[0].provider);
      }
    }).catch(err => {
      // Voice list is non-critical — don't block chat with an aggressive toast
      console.warn('[AgentChat] Failed to load voices (non-critical):', err);
    });
  // Only fetch once on mount; don't refetch when selectedVoiceId changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (callError) toast.error(callError);
  }, [callError]);

  // Click outside to close voice picker
  useEffect(() => {
    if (!showVoicePicker) return;
    const onClick = (e: MouseEvent) => {
      if (voicePickerRef.current && !voicePickerRef.current.contains(e.target as Node)) {
        setShowVoicePicker(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showVoicePicker]);

  // Step 3: Socket 错误恢复机制 - 监听连接状态
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;
    const handleDisconnect = () => {
      setSocketStatus('disconnected');
      toast.error('服务连接断开，正在重试...');
    };
    const handleConnect = () => {
      setSocketStatus('connected');
    };
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleDisconnect);
    socket.on('connect', handleConnect);
    if (!socket.connected) {
      setSocketStatus('disconnected');
    }
    return () => {
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleDisconnect);
      socket.off('connect', handleConnect);
    };
  }, []);

  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const draftTextRef = useRef('');
  // Ref holding the latest sendText so stable callbacks (defined before sendText's
  // const declaration) can call it without triggering a TDZ ReferenceError.
  const sendTextRef = useRef<((text: string, attachments?: ChatAttachment[]) => Promise<void>) | null>(null);
  const [hasDraftText, setHasDraftText] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearchingHistory, setIsSearchingHistory] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus>('idle');
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [backgroundWorkflowTasks, setBackgroundWorkflowTasks] = useState<BackgroundWorkflowTask[]>([]);
  
  // Step 3: Socket 错误恢复机制
  const [socketStatus, setSocketStatus] = useState<'connected'|'disconnected'|'reconnecting'>('connected');
  const [lastDraft, setLastDraft] = useState<string>(() => localStorage.getItem('xf_chat_draft') || '');
  
  const { speak, stop, pause, resume, isSpeaking, isPaused } = useTTS();
  const recognition = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const agentNameRef = useRef<string>('小枫');
  const mermaidRenderedIds = useRef<Set<string>>(new Set());
  const mermaidTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const audioWaveRandoms = useRef([...Array(5)].map(() => Math.random() * 0.5));
  const seenToolEventIds = useRef<Set<string>>(new Set());
  const seenWorkflowToolEvents = useRef<Set<string>>(new Set());
  const backgroundTaskStatusRef = useRef<Map<string, string>>(new Map());

  const upsertBackgroundWorkflowTask = useCallback((task: BackgroundWorkflowTask) => {
    if (!task?.id) return;
    setBackgroundWorkflowTasks(prev => {
      const existing = prev.findIndex(item => item.id === task.id);
      const nextTask = { ...prev[existing], ...task };
      const next = existing >= 0
        ? prev.map(item => item.id === task.id ? nextTask : item)
        : [nextTask, ...prev];
      return next.slice(0, 6);
    });

    if (['completed', 'failed', 'cancelled'].includes(task.status)) {
      window.setTimeout(() => {
        setBackgroundWorkflowTasks(prev => prev.filter(item => item.id !== task.id));
        backgroundTaskStatusRef.current.delete(task.id);
      }, 12000);
    }
  }, []);

  const cancelBackgroundWorkflowTask = useCallback((taskId: string) => {
    socket?.emit('agent:background_cancel', { taskId });
    setBackgroundWorkflowTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, status: 'cancelling' } : task
    ));
  }, [socket]);

  useEffect(() => {
    if (!isOpen) return;
    apiFetch('/api/autonomy/background-tasks', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!Array.isArray(data?.tasks)) return;
        data.tasks.slice(0, 6).forEach((task: BackgroundWorkflowTask) => upsertBackgroundWorkflowTask(task));
      })
      .catch(() => {});
  }, [isOpen, upsertBackgroundWorkflowTask]);

  // 计划B: 草稿持久化 + 工具栏回填回调
  const getDraftKey = (cid) => 'xf_draft_' + (cid || 'new');
  const loadDraft = useCallback((cid) => {
    try { 
      const draft = localStorage.getItem(getDraftKey(cid));
      if (!draft) return '';
      return draft;
    } catch (err) {
      console.warn('[DraftLoad] Failed to load draft for', cid, err instanceof Error ? err.message : String(err));
      return '';
    }
  }, [getDraftKey]);
  const persistDraft = useCallback((cid, text) => {
    try {
      const k = getDraftKey(cid);
      if (!text.trim()) {
        localStorage.removeItem(k);
        return true;
      }
      
      localStorage.setItem(k, text);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      
      // Check for quota exceeded error
      if (errorMessage.includes('QuotaExceededError') || errorMessage.includes('NS_ERROR_STORAGE_QUOTA')) {
        console.error('[DraftPersist] Storage quota exceeded, clearing all drafts...');
        
        // Attempt cleanup
        try {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('xf_draft_')) {
              localStorage.removeItem(key);
            }
          });
          console.log('[DraftPersist] Cleared all drafts successfully');
          
          // Try again with in-memory fallback
          draftCache.current[cid || 'new'] = text;
          console.warn('[DraftPersist] Saved to in-memory cache as fallback');
          return false;
        } catch (cleanupErr) {
          console.error('[DraftPersist] Cleanup failed:', cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr));
          return false;
        }
      }
      
      // Generic error logging
      console.warn(`[DraftPersist] Failed to save draft for ${cid}:`, errorMessage);
      return false;
    }
  }, [getDraftKey]);

const clearAllDrafts = useCallback(() => {
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('xf_draft_')) {
          localStorage.removeItem(key);
        }
      });
      draftCache.current = {};
      console.debug('[DraftPersist] All drafts cleared');
    } catch (err) {
      console.error('[DraftPersist] Failed to clear drafts:', err instanceof Error ? err.message : String(err));
    }
  }, []);
  const clearDraft = (cid) => {
    try { localStorage.removeItem(getDraftKey(cid)); } catch (err) {
      console.warn('[ClearDraft] Failed to clear draft for', cid, err instanceof Error ? err.message : String(err));
    }
  };
  const updateDraftText = useCallback((value: string) => {
    setDraftTextState(value);
    draftTextRef.current = value;
    const nextHasDraft = value.trim().length > 0;
    setHasDraftText(prev => prev === nextHasDraft ? prev : nextHasDraft);
    persistDraft(activeConversationId, value);
  }, [activeConversationId]);

  const setDraftText = useCallback((value: string) => {
    updateDraftText(value);
    if (messageInputRef.current) messageInputRef.current.value = value;
  }, [updateDraftText]);
  const appendToDraft = useCallback((text) => {
    setDraftText((draftTextRef.current || '') + text);
  }, [setDraftText]);
  const handleQuoteMessage = useCallback((text) => {
    if (!text) return;
    appendToDraft('> ' + text.split('\n').join('\n> ') + '\n\n');
  }, [appendToDraft]);
  const handleSkillMention = useCallback((skill) => {
    appendToDraft('@skill ' + skill + ' ');
  }, [appendToDraft]);
  const handleConnectorMention = useCallback((connector) => {
    appendToDraft('@connector ' + connector + ' ');
  }, [appendToDraft]);

  // Escape to close panels
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showVoicePicker) setShowVoicePicker(false);
        if (showWeChatSettings) setShowWeChatSettings(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showVoicePicker, showWeChatSettings]);

  // Keyboard shortcuts for chat input
  useEffect(() => {
    if (!isOpen) return;
    const onShortcut = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        if (e.ctrlKey && e.key === 'Enter') return;
        return;
      }
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        const input = messageInputRef.current;
        if (input && input.value.trim()) {
          sendTextRef.current?.(input.value.trim());
          input.value = '';
          updateDraftText('');
        }
      }
      if (e.key === 'Escape') {
        if (showVoicePicker) setShowVoicePicker(false);
        else if (showWeChatSettings) setShowWeChatSettings(false);
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [isOpen, showVoicePicker, showWeChatSettings, updateDraftText]);

  // Cleanup mermaid timers on unmount (Bug#4 fix)
  useEffect(() => {
    return () => {
      mermaidTimers.current.forEach(clearTimeout);
      mermaidTimers.current.clear();
    };
  }, []);

  const agentName = agent?.name || (t.lumiEssence || '小枫 Essence');
  const agentCategory = agent?.category || (t.friend || '好友');
  const agentId = agent?.id || 'xiaofeng';
    
  // P0 Fix: personalityId validation with logging to prevent ReferenceError crash
  const personalityId = (() => {
    const pid = (agent as any)?.personalityId;
    if (!pid || typeof pid !== 'string' || pid.trim() === '') {
      console.warn('[AgentChatPage] Missing or invalid personalityId, defaulting to "xiaofeng"');
      return 'xiaofeng';
    }
    return pid.trim();
  })();
    
  const toolFailureHint = t.toolFailureHint || 'Check permission, adjust the request, or ask 小枫 to retry.';
  const scopedConversationUrl = useCallback((path: string) => {
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}domain=${encodeURIComponent(activeDomain)}&agentId=${encodeURIComponent(agentId)}`;
  }, [activeDomain, agentId]);
  const scopedFileUrl = useCallback((path: string) => {
    const separator = path.includes('?') ? '&' : '?';
    const orgScope = activeDomain === 'work' && activeOrgId
      ? `&orgId=${encodeURIComponent(activeOrgId)}`
      : '';
    return `${path}${separator}domain=${encodeURIComponent(activeDomain)}${orgScope}`;
  }, [activeDomain, activeOrgId]);
  const requestMeetingMode = useCallback(() => {
    window.dispatchEvent(new CustomEvent('xf:request-meeting-mode'));
  }, []);

  const isFounder = agentId === 'founder' || agentCategory === 'founder' || agentName.includes('Founder') || agentName.includes('创始人');

  useEffect(() => { agentNameRef.current = agentName; }, [agentName]);
  // v3.12.2: socket effect ref syncs — 保持 ref 与最新值同步，避免重注册
  useEffect(() => { agentIdRef.current = agentId; }, [agentId]);
  useEffect(() => { autoSpeakReplyRef.current = autoSpeakReply; }, [autoSpeakReply]);
  useEffect(() => { activeConversationIdRef.current = activeConversationId; }, [activeConversationId]);
  useEffect(() => { onConversationChangeRef.current = onConversationChange; }, [onConversationChange]);
  useEffect(() => { upsertBackgroundWorkflowTaskRef.current = upsertBackgroundWorkflowTask; }, [upsertBackgroundWorkflowTask]);

  useEffect(() => {
    // Initialize Speech Recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = false;
      recognition.current.interimResults = false;
      recognition.current.lang = 'zh-CN'; // Default to Chinese, can be dynamic

      recognition.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setDraftText(transcript);
        setIsListening(false);
      };

      recognition.current.onerror = (event: any) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
        toast.error(`${t.speechNotSupported || '语音识别错误'}: ${event.error}`);
      };

      recognition.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [setDraftText, t.speechNotSupported]);

  const handleCopyMessage = useCallback(async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {}
  }, []);

  // P-7: stable callbacks for .map() items using data attributes
  const handleVoiceSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const voiceId = e.currentTarget.dataset.voiceId;
    if (voiceId) {
      const voice = voices.find(v => v.voiceId === voiceId);
      setCtxSelectedVoiceId(voiceId, voice?.provider);
      setShowVoicePicker(false);
    }
  }, [voices]);

  const handleSuggestionSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const prompt = e.currentTarget.dataset.prompt;
    if (prompt) sendTextRef.current?.(prompt);
  }, []);

  const handleMessageCopy = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const text = e.currentTarget.dataset.copyText;
    const msgId = e.currentTarget.dataset.msgId;
    if (text && msgId) handleCopyMessage(text, msgId);
  }, [handleCopyMessage]);

  const toggleMsgSelect = useCallback((msgId: string) => {
    setSelectedMsgIds(function(prev) {
      var next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId); else next.add(msgId);
      return next;
    });
  }, []);


  const handleEditMessage = useCallback((msgId: string) => { setEditingMsgId(msgId); }, []);

  const handleEditCancel = useCallback(() => { setEditingMsgId(null); }, []);

  const handleEditSubmit = useCallback((msgId: string, newText: string) => {
    setEditingMsgId(null);
    const idx = messages.findIndex(m => m.id === msgId);
    if (idx === -1) return;
    setMessages(messages.slice(0, idx));
    sendTextRef.current?.(newText);
  }, [messages]);

  const handleRegenerate = useCallback(() => {
    const lastUserMsg = [...messages].reverse().find(m => m.type === 'user');
    if (!lastUserMsg) return;
    const text = getDisplayText(lastUserMsg);
    const lastUserIdx = messages.findIndex(m => m.id === lastUserMsg.id);
    if (lastUserIdx === -1) return;
    setMessages(messages.slice(0, lastUserIdx + 1));
    sendTextRef.current?.(text);
  }, [messages]);

  // P1 Fix: Enhanced socket cancel helper with proper error handling and connection checking
  const emitSocketCancel = useCallback((conversationId: string, requestId: string | null) => {
    if (!socket) {
      console.warn('[Socket] Not initialized, cannot cancel active chat');
      return;
    }
    
    if (!socket.connected) {
      console.warn(`[Socket] Not connected to server, cannot cancel conversation ${conversationId}`);
      return;
    }
    
    try {
      socket.emit('agent:cancel', { 
        conversationId, 
        requestId 
      }, (err?: any) => {
        if (err) {
          console.error(`[Cancel] Server rejected cancellation for ${conversationId}:`, err.message || err);
        } else {
          console.debug(`[Cancel] Successfully cancelled conversation ${conversationId}`);
        }
      });
    } catch (error) {
      console.error(`[Cancel] Exception during emission:`, error instanceof Error ? error.message : String(error));
    }
  }, [socket]);

  const handleFeedback = useCallback((msgId: string, type: 'like' | 'dislike') => {
    setFeedbackMap(prev => {
      const current = prev[msgId];
      return { ...prev, [msgId]: current === type ? null : type };
    });
  }, []);

  const handleCodeCopy = useCallback(async (codeText: string) => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCodeCopied(codeText.slice(0, 20));
      setTimeout(() => setCodeCopied(null), 1500);
    } catch {}
  }, []);

  const commitTitleEdit = useCallback(() => {
    setTitleEditing(false);
    const newName = titleDraft.trim();
    if (newName && newName !== agentName) {
      agentNameRef.current = newName;
      if (activeConversationId) {
        conversationService.patchConversation(activeConversationId, { title: newName }).catch(() => {});
      }
    }
  }, [titleDraft, agentName, activeConversationId]);

  const renderGeneratedFiles = useCallback((files: GeneratedFileLink[], align: 'start' | 'end' = 'start') => {
    if (files.length === 0) return null;
    const labelFor = (kind: GeneratedFileLink['kind']) => {
      if (kind === 'deck') return ui('演示文稿', 'Presentation');
      if (kind === 'sheet') return ui('表格', 'Spreadsheet');
      if (kind === 'pdf') return 'PDF';
      if (kind === 'cad') return 'CAD';
      if (kind === 'image') return ui('图片', 'Image');
      if (kind === 'document') return ui('文档', 'Document');
      return ui('文件', 'File');
    };

    return (
      <div className={`max-w-[92%] mb-3 flex flex-wrap gap-2 ${align === 'end' ? 'justify-end' : 'justify-start'}`}>
        {files.map(file => (
          <a
            key={file.id}
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex min-w-0 max-w-[280px] items-center gap-3 rounded-2xl border border-emerald-400/15 bg-emerald-400/10 px-3 py-2.5 text-left transition-all hover:border-emerald-300/35 hover:bg-emerald-400/15"
            title={file.path}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-200">
              {file.kind === 'image' ? <ImageIcon size={17} /> : <FileText size={17} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-white/80">{file.fileName}</div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-emerald-100/55">
                <Download size={11} />
                <span>{labelFor(file.kind)}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    );
  }, [isZh]);

  const buildSearchDisplayMessages = useCallback(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return messages;

    const localMatches = messages.filter(m =>
      m.text && String(m.text).toLowerCase().includes(query)
    );
    const seen = new Set<string>();
    const merged = [...localMatches, ...searchResults].filter((m) => {
      const key = `${m.id || ''}|${m.timestamp || ''}|${m.type || ''}|${m.text || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return merged;
  }, [messages, searchQuery, searchResults]);

  const searchDisplayMessages = buildSearchDisplayMessages();
  const renderWindow = CHAT_RENDER_LIMIT + earlierOffset;
  const displayMessages = searchQuery.trim()
    ? searchDisplayMessages
    : messages.slice(-renderWindow);
  const hiddenMessageCount = searchQuery.trim()
    ? 0
    : Math.max(0, messages.length - displayMessages.length);

  const handleBatchCopy = useCallback(function() {
    var texts = displayMessages.filter(function(m) { return selectedMsgIds.has(m.id); }).map(function(m) { return getDisplayText(m); });
    navigator.clipboard.writeText(texts.join('\n\n---\n\n')).catch(function(){});
    setSelectedMsgIds(new Set());
    setMsgSelectMode(false);
  }, [displayMessages, selectedMsgIds]);



  const normalizePersistedMessages = useCallback((rawMessages: any[]) => {
    const normalized: any[] = [];
    const userName = user?.displayName || user?.username || (t.chatUserFallback || '用户');
    const agentDisplayName = agentNameRef.current || '小枫';

    const pushMessage = (message: any) => {
      if (!message.text || !String(message.text).trim()) return;
      normalized.push(message);
    };

    rawMessages.forEach((m: any, index: number) => {
      const baseId = m.id || `persisted-${index}`;
      const timestamp = m.timestamp || m.createdAt || new Date().toISOString();
      const role = m.role || '';
      const userText = role === 'assistant' ? '' : (m.content || m.message || '');
      const assistantText = role === 'assistant'
        ? (m.content || m.message || m.response || '')
        : (m.response || '');

      if (role !== 'tool' && userText) {
        pushMessage({
          id: `${baseId}-user`,
          text: userText,
          userName,
          timestamp,
          type: 'user',
          mode: m.mode,
        });
      }

      if (assistantText) {
        pushMessage({
          id: `${baseId}-assistant`,
          text: assistantText,
          userName: agentDisplayName,
          timestamp,
          type: 'agent',
          mode: m.mode,
        });
      }
    });

    const seen = new Set<string>();
    return normalized.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [user?.displayName, user?.username, t.chatUserFallback]);

  // ── Load conversation by explicit conversationId ──
  // Only loads when conversationId prop is provided (not null).
  // When conversationId is null → blank new conversation, no history loaded.
  // This replaces the old auto-load of /api/conversations/active which caused
  // 107K token bloat on every visit.
  useEffect(() => {
    if (!agentId || isFounder) return;

    // Track previous conversationId to detect switches
    if (conversationId === undefined) {
      // Legacy mode: no conversationId prop passed, use old behavior (auto-load active)
      // This keeps backward compatibility for non-AppLayout entry points
      if (initialLoadDoneRef.current) return;
      initialLoadDoneRef.current = true;
      apiFetch(scopedConversationUrl('/api/conversations/active'))
        .then(r => r.json())
        .then(async (data) => {
          const conv = data.activeConversation;
          if (conv) {
            setActiveConversationId(conv.id);
            const msgRes = await apiFetch(scopedConversationUrl(`/api/conversations/${conv.id}/messages?limit=${CHAT_HISTORY_LIMIT}`));
            const msgData = await msgRes.json();
            if (msgData.messages && Array.isArray(msgData.messages)) {
              setMessages(normalizePersistedMessages(msgData.messages));
              const saved = loadDraft(conv.id);
              if (saved) setDraftText(saved);
            }
          }
        })
        .catch(() => {});
      return;
    }

    // conversationId prop is defined → explicit control mode
    if (conversationId === null) {
      // Blank new conversation — clear messages, no API call
      console.log('[AgentChatPage] New blank conversation (conversationId=null)');
      // 🔴 v3.12.55: Cancel any in-flight request BEFORE resetting state
      if (textChatActiveRef.current && activeConversationId) {
        emitSocketCancel(activeConversationId, activeChatRequestIdRef.current)
      }
      setMessages([]);
      setActiveConversationId(null);
      clearDraft(null);
      // Reset ALL chat states — v3.12.54 missed textChatActiveRef/activeChatRequestIdRef/seenWorkflowToolEvents
      textChatActiveRef.current = false;
      activeChatRequestIdRef.current = null;
      seenWorkflowToolEvents.current.clear();
      setIsTyping(false);
      setWorkflowStatus('idle');
      setWorkflowSteps([]);
      streamingMsgId.current = null;
      chunkBufferRef.current = '';
      if (chunkRafRef.current) { cancelAnimationFrame(chunkRafRef.current); chunkRafRef.current = null; }
      initialLoadDoneRef.current = true;
      return;
    }

    // Specific conversation ID — load its messages
    if (conversationId && conversationId !== lastLoadedConvIdRef.current) {
      lastLoadedConvIdRef.current = conversationId;
      initialLoadDoneRef.current = true;
      console.log('[AgentChatPage] Loading conversation:', conversationId);
      // 🔴 v3.12.55: Cancel in-flight request + reset ALL refs (v3.12.54 missed 5 refs)
      if (textChatActiveRef.current && activeConversationId) {
        emitSocketCancel(activeConversationId, activeChatRequestIdRef.current)
      }
      textChatActiveRef.current = false;
      activeChatRequestIdRef.current = null;
      seenWorkflowToolEvents.current.clear();
      setIsTyping(false);
      setWorkflowStatus('idle');
      setWorkflowSteps([]);
      streamingMsgId.current = null;
      chunkBufferRef.current = '';
      if (chunkRafRef.current) { cancelAnimationFrame(chunkRafRef.current); chunkRafRef.current = null; }
      setMessages([]);
      setActiveConversationId(conversationId);
      apiFetch(scopedConversationUrl(`/api/conversations/${conversationId}/messages?limit=${CHAT_HISTORY_LIMIT}`))
        .then(r => r.json())
        .then((msgData) => {
          if (msgData.messages && Array.isArray(msgData.messages)) {
            setMessages(normalizePersistedMessages(msgData.messages));
          }
          const saved2 = loadDraft(conversationId);
          if (saved2) setDraftText(saved2);
        })
        .catch(() => {});
    }
  }, [agentId, isFounder, normalizePersistedMessages, scopedConversationUrl, conversationId]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query || !agentId || isFounder) {
      setSearchResults([]);
      setIsSearchingHistory(false);
      setSearchError('');
      return;
    }

    let cancelled = false;
    setIsSearchingHistory(true);
    setSearchError('');

    const timer = setTimeout(() => {
      apiFetch(scopedConversationUrl(`/api/conversations/search?q=${encodeURIComponent(query)}&limit=${CHAT_SEARCH_LIMIT}`))
        .then(async r => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(data.error || '搜索失败');
          if (!cancelled) {
            setSearchResults(normalizePersistedMessages(data.results || []));
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setSearchResults([]);
            setSearchError(((err) as any).message || '搜索失败');
          }
        })
        .finally(() => {
          if (!cancelled) setIsSearchingHistory(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [agentId, isFounder, normalizePersistedMessages, scopedConversationUrl, searchQuery]);

  const streamingMsgId = useRef<string | null>(null);
  const textChatActiveRef = useRef(false);
  const activeChatRequestIdRef = useRef<string | null>(null);
  const initialLoadDoneRef = useRef(false);
  const lastAgentIdRef = useRef<string>('');
  const lastLoadedConvIdRef = useRef<string | null>(null);
  const chunkBufferRef = useRef<string>('');
  const chunkRafRef = useRef<number | null>(null);
  // v3.12.2: socket effect 用 ref 持有频繁变化的值，避免重注册 socket 监听器
  const agentIdRef = useRef(agentId);
  const autoSpeakReplyRef = useRef(autoSpeakReply);
  const activeConversationIdRef = useRef(activeConversationId);
  const onConversationChangeRef = useRef(onConversationChange);
  const upsertBackgroundWorkflowTaskRef = useRef(upsertBackgroundWorkflowTask);

  useEffect(() => {
    if (isFounder || !socket) return;

    const isCurrentChatEvent = (data?: { requestId?: string; source?: string }) => {
      if (data?.requestId) return data.requestId === activeChatRequestIdRef.current;
      if (data?.source && data.source !== 'chat') return false;
      return textChatActiveRef.current;
    };

    const onProactive = (data: { message: string; timestamp: string; requestId?: string; source?: string; type?: string; taskId?: string }) => {
      const proactiveType = data.type || data.taskId;
      if (proactiveType === 'greeting' && localStorage.getItem('xf_allow_proactive_voice') !== 'true') return;
      if ((data.requestId || data.source) && !isCurrentChatEvent(data)) return;
      setMessages(prev => {
        if (prev.some(m => m.text === data.message && m.type === 'agent')) return prev;
        return [...prev, {
          id: `proactive-${Date.now()}`,
          text: data.message,
          // v3.12.2: 用 agentNameRef.current 避免 socket effect 闭包陈旧
          userName: agentNameRef.current,
          timestamp: data.timestamp || new Date().toISOString(),
          type: 'agent',
          source: 'proactive',
        }];
      });
    };

    const flushChunk = () => {
      chunkRafRef.current = null;
      const buf = chunkBufferRef.current;
      chunkBufferRef.current = '';
      if (!buf) return;
      if (streamingMsgId.current) {
        const sid = streamingMsgId.current;
        setMessages(prev => prev.map(m =>
          m.id === sid ? { ...m, text: m.text + buf } : m
        ));
      } else {
        const id = Date.now().toString();
        streamingMsgId.current = id;
        setMessages(prev => [...prev, {
          id,
          text: buf,
          userName: agentNameRef.current,
          timestamp: new Date().toISOString(),
          type: 'agent'
        }]);
      }
    };

    const onChunk = (data: { text: string; agentName: string; requestId?: string; source?: string }) => {
      if (!isCurrentChatEvent(data)) return;
      chunkBufferRef.current += data.text;
      if (chunkRafRef.current == null) {
        chunkRafRef.current = requestAnimationFrame(flushChunk);
      }
    };

    const onTool = (data: { correlationId?: string; name: string; args?: any; arguments?: any; result?: string; error?: string; requestId?: string; source?: string }) => {
      if (!isCurrentChatEvent(data)) return;
      const eventId = data.correlationId || `tool-${Date.now()}-${crypto.randomUUID()}`;
      const args = data.arguments ?? data.args;
      const status = data.error ? 'error' : (data.result !== undefined ? 'done' : 'running');
      const phase = data.error !== undefined ? 'error' : data.result !== undefined ? 'result' : 'start';
      const nextMessage = {
        id: eventId,
        userName: data.name,
        text: data.error || data.result || '',
        timestamp: new Date().toISOString(),
        type: 'tool',
        toolName: data.name,
        toolArgs: args,
        toolResult: data.result,
        toolError: data.error,
        toolStatus: status,
      };

      setMessages(prev => {
        const existingIndex = prev.findIndex(m => m.id === eventId);
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...nextMessage };
          return updated;
        }
        if (seenToolEventIds.current.has(eventId)) return prev;
        seenToolEventIds.current.add(eventId);
        return [...prev, nextMessage];
      });

      if (data.correlationId) {
        const workflowEventKey = `${data.correlationId}:${phase}`;
        if (seenWorkflowToolEvents.current.has(workflowEventKey)) return;
        seenWorkflowToolEvents.current.add(workflowEventKey);
      }

      setWorkflowStatus('executing');
      if (data.result !== undefined) {
        setWorkflowSteps(prev => [...prev, {
          id: `chat-tool-ok-${Date.now()}-${crypto.randomUUID()}`,
          type: 'tool_result',
          text: `${data.name} ${t.workflowToolDone || '已完成'}`,
          detail: data.result?.slice(0, 100),
          time: Date.now(),
        }]);
      } else if (data.error !== undefined) {
        setWorkflowSteps(prev => [...prev, {
          id: `chat-tool-err-${Date.now()}-${crypto.randomUUID()}`,
          type: 'error',
          text: `${data.name} ${t.workflowToolFailed || '失败'}`,
          detail: data.error?.slice(0, 100),
          time: Date.now(),
        }]);
      } else {
        const argsSummary = args
          ? Object.entries(args).map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 30) : String(v).slice(0, 30)}`).join(', ')
          : '';
        setWorkflowSteps(prev => [...prev, {
          id: `chat-tool-start-${Date.now()}-${crypto.randomUUID()}`,
          type: 'tool_start',
          text: `${t.workflowCalling || '正在调用'} ${data.name}`,
          detail: argsSummary || undefined,
          time: Date.now(),
        }]);
      }
    };

    const onConfirmTool = (data: { correlationId: string; name: string; arguments?: any; requestId?: string; source?: string }) => {
      if (!isCurrentChatEvent(data)) return;
      setWorkflowStatus('waiting_confirmation');
      const argsSummary = data.arguments
        ? Object.entries(data.arguments).map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 30) : String(v).slice(0, 30)}`).join(', ')
        : '';
      setWorkflowSteps(prev => [...prev, {
        id: `chat-confirm-${data.correlationId || Date.now()}`,
        type: 'confirmation',
        text: `${t.workflowWaitingConfirm || '等待批准'}: ${data.name}`,
        detail: argsSummary || (t.workflowConfirmHint || '请查看权限对话框以继续。'),
        time: Date.now(),
      }]);
    };

    const onResponse = (data: { text: string; agentName: string; source?: string; requestId?: string }) => {
      if (!isCurrentChatEvent(data)) return;
      setIsTyping(false);
      setWorkflowStatus('done');
      setWorkflowSteps(prev => [...prev, {
        id: `chat-resp-${Date.now()}`,
        type: 'response',
        text: t.workflowResponseReady || '响应就绪',
        detail: data.text?.slice(0, 100),
        time: Date.now(),
      }]);
      setTimeout(() => {
        setWorkflowStatus('idle');
        setWorkflowSteps([]);
        seenWorkflowToolEvents.current.clear();
      }, 5000);
      if (streamingMsgId.current) {
        // Finalize streamed message; keep chunked text if response text is empty.
        const finalText = (data.text && data.text.trim()) ? data.text : null;
        setMessages(prev => prev.map(m =>
          m.id === streamingMsgId.current
            ? { ...m, text: finalText || m.text }
            : m
        ));
        streamingMsgId.current = null;
        // Auto-speak the response (仅当用户开启「自动语音回复」)
        if (finalText && autoSpeakReply) speak(finalText);
      } else if (data.text && data.text.trim()) {
        // No streaming; add as new message only if non-empty and not already answered by REST fallback.
        const restId = 'rest-' + (activeChatRequestIdRef.current || '');
        setMessages(prev => {
          if (prev.some(m => m.id === restId)) return prev;
          return [...prev, {
            id: Date.now().toString(),
            text: data.text,
            userName: data.agentName,
            timestamp: new Date().toISOString(),
            type: 'agent'
          }];
        });
        // Auto-speak the response (仅当用户开启「自动语音回复」)
        if (autoSpeakReplyRef.current) speak(data.text);
      }
    };

    const onStatus = (data: { status: string; requestId?: string; source?: string }) => {
      // idle/error are terminal signals — always process them regardless of chat state
      // (because resolve() sets textChatActiveRef=false BEFORE the idle event arrives)
      const isTerminal = data.status === 'idle' || data.status === 'error';
      if (!isTerminal && !isCurrentChatEvent(data)) return;
      setIsTyping(data.status === "thinking");
      if (data.status === 'thinking') {
        setWorkflowStatus('thinking');
        setWorkflowSteps(prev => {
          const last = prev[prev.length - 1];
          if (last?.type === 'thinking' && Date.now() - last.time < 1200) return prev;
          return [...prev, {
            id: `chat-thinking-${Date.now()}`,
            type: 'thinking',
            text: t.workflowAnalyzing || '正在分析你的请求...',
            time: Date.now(),
          }];
        });
      } else if (data.status === 'idle') {
        setWorkflowStatus('done');
        setWorkflowSteps(prev => [...prev, {
          id: `chat-done-${Date.now()}`,
          type: 'response',
          text: t.workflowCompleted || '已完成',
          time: Date.now(),
        }]);
        setTimeout(() => {
          setWorkflowStatus('idle');
          setWorkflowSteps([]);
          seenWorkflowToolEvents.current.clear();
        }, 5000);
      } else if (data.status === 'error') {
        setWorkflowStatus('error');
        setTimeout(() => {
          setWorkflowStatus('idle');
          setWorkflowSteps([]);
          seenWorkflowToolEvents.current.clear();
        }, 5000);
      }
      if (data.status === "idle" || data.status === "error") {
        // Drop partial streaming chunks that were never finalized
        if (streamingMsgId.current) {
          const sid = streamingMsgId.current;
          setMessages(prev => prev.filter(m => m.id !== sid));
          streamingMsgId.current = null;
        }
      }
    };

    const onError = (data: { message: string; code?: string; requestId?: string; source?: string }) => {
      if (!isCurrentChatEvent(data)) return;
      setIsTyping(false);
      setWorkflowStatus('error');
      setWorkflowSteps(prev => [...prev, {
        id: `chat-err-${Date.now()}`,
        type: 'error',
        text: t.workflowError || '处理失败',
        detail: data.message,
        time: Date.now(),
      }]);
      setTimeout(() => {
        setWorkflowStatus('idle');
        setWorkflowSteps([]);
        seenWorkflowToolEvents.current.clear();
      }, 5000);
      if (streamingMsgId.current) {
        const sid = streamingMsgId.current;
        setMessages(prev => prev.filter(m => m.id !== sid));
        streamingMsgId.current = null;
      }
      const message = data.message || (t.failedToRouteNeuralMesh || '无法通过神经网格路由。');
      setMessages(prev => {
        const text = `${t.requestFailed || '请求失败'}\n\n${message}`;
        if (prev.some(m => m.type === 'agent' && m.text === text)) return prev;
        return [...prev, {
          id: `err-${Date.now()}-${crypto.randomUUID()}`,
          text,
          userName: agentNameRef.current || '小枫',
          timestamp: new Date().toISOString(),
          type: 'agent',
          source: 'error',
        }];
      });
      toast.error(message);
    };

    // conversation_updated: only reload for non-text-chat channels (voice, etc.)
    // Text chat state is managed live via agent:chunk/agent:response; API reload here
    // would replace messages with different ids, causing React to remount & re-animate them.
    const onConversationUpdated = (data: { conversationId: string; agentId: string; source?: string }) => {
      if (data.agentId !== agentIdRef.current) return;
      // Notify parent of new conversation (for sidebar refresh)
      if (data.conversationId && !activeConversationIdRef.current) {
        setActiveConversationId(data.conversationId);
        onConversationChangeRef.current?.(data.conversationId);
        window.dispatchEvent(new CustomEvent('xf:conversation-created', {
          detail: { conversationId: data.conversationId },
        }));
      }
      if (data.source === 'chat' || textChatActiveRef.current) {
        window.dispatchEvent(new Event('xf:refresh-conversations'));
        return;
      }
      if (streamingMsgId.current) streamingMsgId.current = null;
      apiFetch(scopedConversationUrl(`/api/conversations/${data.conversationId}/messages?limit=${CHAT_HISTORY_LIMIT}`))
        .then(r => r.json())
        .then(result => {
          if (result.messages && Array.isArray(result.messages)) {
            setMessages(normalizePersistedMessages(result.messages));
          }
        })
        .catch(() => {});
    };

    const normalizeBackgroundTask = (data: any): BackgroundWorkflowTask | null => {
      const raw = data?.task || data;
      const id = String(raw?.id || data?.taskId || '');
      if (!id) return null;
      const workerNames = Array.isArray(raw?.workerNames)
        ? raw.workerNames
        : Array.isArray(raw?.workers)
          ? raw.workers.map((worker: any) => worker?.name || worker?.id).filter(Boolean)
          : [];
      return {
        id,
        title: raw?.title || data?.title || id,
        status: (raw?.status || 'queued') as BackgroundWorkflowTask['status'],
        workerNames,
        toolCallsCount: Number(raw?.toolCallsCount || 0),
        error: raw?.error,
        resultPreview: raw?.resultPreview,
        updatedAt: raw?.updatedAt,
      };
    };

    const recordBackgroundTaskStep = (task: BackgroundWorkflowTask) => {
      const previousStatus = backgroundTaskStatusRef.current.get(task.id);
      if (previousStatus === task.status) return;
      backgroundTaskStatusRef.current.set(task.id, task.status);
      const isActive = task.status === 'queued' || task.status === 'running' || task.status === 'cancelling';
      const isFailed = task.status === 'failed';
      setWorkflowStatus(isActive ? 'background' : isFailed ? 'error' : 'done');
      setWorkflowSteps(prev => [...prev, {
        id: `chat-background-task-${task.id}-${task.status}-${Date.now()}`,
        type: isFailed ? 'error' : task.status === 'completed' ? 'response' : 'background',
        text: `${t.workflowBackgroundTask || '后台任务'}: ${task.status}`,
        detail: task.title || task.id,
        time: Date.now(),
      }]);
    };

    const onDelegation = (data: any) => {
      const task = normalizeBackgroundTask(data);
      if (!task) return;
      upsertBackgroundWorkflowTaskRef.current(task);
      recordBackgroundTaskStep(task);
    };

    const onBackgroundTaskUpdate = (data: any) => {
      const task = normalizeBackgroundTask(data);
      if (!task) return;
      upsertBackgroundWorkflowTaskRef.current(task);
      recordBackgroundTaskStep(task);
    };

    socket.on("agent:proactive", onProactive);
    socket.on("agent:delegation", onDelegation);
    socket.on("agent:background_task_update", onBackgroundTaskUpdate);
    socket.on("agent:chunk", onChunk);
    socket.on("agent:tool", onTool);
    socket.on("agent:tool_call", onTool);
    socket.on("agent:confirm_tool", onConfirmTool);
    socket.on("agent:response", onResponse);
    socket.on("agent:status", onStatus);
    socket.on("agent:error", onError);
    socket.on("chat:conversation_updated", onConversationUpdated);

    return () => {
      socket.off("agent:proactive", onProactive);
      socket.off("agent:delegation", onDelegation);
      socket.off("agent:background_task_update", onBackgroundTaskUpdate);
      socket.off("agent:chunk", onChunk);
      socket.off("agent:tool", onTool);
      socket.off("agent:tool_call", onTool);
      socket.off("agent:confirm_tool", onConfirmTool);
      socket.off("agent:response", onResponse);
      socket.off("agent:status", onStatus);
      socket.off("agent:error", onError);
      socket.off("chat:conversation_updated", onConversationUpdated);
      if (chunkRafRef.current != null) cancelAnimationFrame(chunkRafRef.current);
      stop();
    };
  // v3.12.2: agentId/agentName/autoSpeakReply/activeConversationId/onConversationChange/upsertBackgroundWorkflowTask
  // 改用 ref 持有，避免 socket effect 闭包陈旧 + 重注册
  }, [speak, stop, isFounder, socket, normalizePersistedMessages, scopedConversationUrl]);

  useEffect(() => {
    // Scroll to bottom when messages change (new messages, initial load)
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [messages]);

  // Scroll to bottom on mount when messages first load
  // v3.12.2: 加 messages 依赖，确保异步加载完成后也能滚动
  useEffect(() => {
    if (messages.length > 0 && scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [isOpen, messages]);

  useEffect(() => {
    if (isOpen) return;
    setWorkflowStatus('idle');
    setWorkflowSteps([]);
    seenWorkflowToolEvents.current.clear();
  }, [isOpen]);

  const sendText = async (text: string, attachments: ChatAttachment[] = pendingAttachments) => {
    const trimmedText = text.trim();

    // Reset intent data for new message
    setIntentData(null);

    // 🎯 P3 Step 1: Intent Classification & Smart Routing (1.5s 超时 fallback，2026-08-11 修复)
    // 之前 classifyIntent 内部会 await 加载本地 embedding 模型（@xenova/transformers），
    // 首次访问需下载 470MB → 卡住整个 sendText，消息发不出去。
    // 现在加 1.5s 超时，超时立即用 default classification 继续走 socket.emit。
    let intentData: IntentClassification | null = null;
    try {
      const startTime = Date.now();
      const intentPromise = classifyIntent(trimmedText, {
        userId: user?.id,
        conversationHistory: messages.slice(-10),
      });
      const timeoutPromise = new Promise<IntentClassification>((resolve) => {
        setTimeout(() => resolve(null as any), 1500);
      });
      intentData = await Promise.race([intentPromise, timeoutPromise]);
      if (intentData) {
        console.log('[Intent] Classified:', intentData.intent, '| Confidence:', intentData.confidence.toFixed(2), '| Model:', intentData.suggestedModel);
        setIntentData(intentData);
        if (intentData.requiresTools.length > 0 && intentData.confidence > 0.85) {
          console.log('[Intent] Auto-invoking tools:', intentData.requiresTools);
          for (const toolName of intentData.requiresTools) {
            try {
              toast.info(`正在调用工具：${toolName}`);
              const toolResponse = await apiFetch('/api/tools/execute', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  toolName,
                  entities: intentData.entities || [],
                  context: trimmedText,
                }),
              });
              setMessages(prev => [...prev, {
                id: `tool-${Date.now()}-${crypto.randomUUID()}`,
                text: `🔧 工具 ${toolName} 执行成功`,
                metadata: { toolResult: toolResponse },
                type: 'system',
              }]);
            } catch (error) {
              console.error(`Tool execution failed: ${toolName}`, error);
              toast.error(`工具 ${toolName} 执行失败`);
            }
          }
        }
        const processingTime = Date.now() - startTime;
        console.log(`[Intent] Processing time: ${processingTime}ms, Layers used:`, intentData.metadata.layersUsed.join(' → '));
      } else {
        console.log('[Intent] 1.5s 超时 fallback（不阻塞消息发送）');
      }
    } catch (error) {
      console.warn('[Intent] Classification failed:', error);
    }

    const outgoingAttachments = attachments.map(item => ({
      id: item.id,
      fileName: item.fileName,
      path: item.path,
      content: item.content || null,
      preview: item.preview || null,
      mimeType: item.mimeType || '',
      size: item.size || 0,
      kind: item.kind,
      downloadUrl: item.downloadUrl,
    }));
    if ((!trimmedText && outgoingAttachments.length === 0) || !user) return;
    const outgoingText = trimmedText || ui('请帮我看看这些附件。', 'Please review these attachments.');

    const userMsg = {
      id: Date.now().toString(),
      text: outgoingText,
      attachments: outgoingAttachments,
      userName: user.displayName || user.username || (t.chatUserFallback || '用户'),
      timestamp: new Date().toISOString(),
      type: 'user'
    };
    textChatActiveRef.current = true;
    seenWorkflowToolEvents.current.clear();
    setWorkflowStatus('thinking');
    setWorkflowSteps([{
      id: `chat-start-${Date.now()}`,
      type: 'thinking',
      text: t.workflowAnalyzing || '正在分析你的请求...',
      time: Date.now(),
    }]);

    setMessages(prev => [...prev, userMsg]);
    clearDraft(activeConversationId);
    setDraftText('');
    setPendingAttachments(prev => prev.filter(item => !outgoingAttachments.some(sent => sent.id === item.id)));
    stop();
    setIsTyping(true);
    const requestId = `chat_${Date.now()}_${crypto.randomUUID()}`;
    activeChatRequestIdRef.current = requestId;

    let resolved = false;
    let safetyTimer: ReturnType<typeof setTimeout>;
    let restFallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const isCurrentResponse = (data?: { requestId?: string; source?: string }) => {
      if (data?.requestId) return data.requestId === requestId;
      if (data?.source && data.source !== 'chat') return false;
      return true;
    };
    const cleanupSocketWaiters = () => {
      socket.off('agent:response', onResponse);
      socket.off('agent:error', onError);
      socket.off('agent:status', onStatus);
    };
    const resolve = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(safetyTimer);
      if (restFallbackTimer) clearTimeout(restFallbackTimer);
      cleanupSocketWaiters();
      setIsTyping(false);
      textChatActiveRef.current = false;
      if (activeChatRequestIdRef.current === requestId) activeChatRequestIdRef.current = null;
    };
    const onResponse = (data?: { requestId?: string; source?: string }) => { if (isCurrentResponse(data)) resolve(); };
    const onError = (data?: { requestId?: string; source?: string }) => { if (isCurrentResponse(data)) resolve(); };
    const onStatus = (data: { status: string; requestId?: string; source?: string }) => {
      if (!isCurrentResponse(data)) return;
      if (data.status === 'idle' || data.status === 'error') resolve();
    };
    safetyTimer = setTimeout(() => {
      if (!resolved) {
        streamingMsgId.current = null;
        // 🔴 v3.12.55: Also clear workflow panel on timeout (was keeping stale thinking state)
        if (textChatActiveRef.current) {
          setWorkflowStatus('idle');
          setWorkflowSteps([]);
        }
        resolve();
      }
    }, outgoingAttachments.length > 0 ? 60000 : 30000);

    socket.on('agent:response', onResponse);
    socket.on('agent:error', onError);
    socket.on('agent:status', onStatus);

    // Always try socket first
    socket.emit("agent:chat", {
      text: outgoingText,
      attachments: outgoingAttachments,
      history: buildChatHistoryPayload(messages),
      personalityId,
      category: agentCategory,
      agentId,
      domain: activeDomain,
      orgId: activeOrgId || null,
      source: 'chat',
      requestId,
      // Enhance with intent data for smart routing
      ...(intentData && {
        intentData: {
          intent: intentData.intent,
          subtype: intentData.subtype,
          suggestedModel: intentData.suggestedModel,
          priority: intentData.priority,
          entities: intentData.entities,
        },
      }),
    });

    // Parallel REST fallback after 5s if socket hasn't responded. It is text-only,
    // so attachments wait for the socket path that preserves file context.
    restFallbackTimer = outgoingAttachments.length === 0 ? setTimeout(async () => {
      if (resolved) return;
      try {
        const response = await runAgentLogic(outgoingText, { platform, aiConfig });
        if (resolved) return;
        resolve();
        // REST fallback 成功：同步清理工作流面板，避免卡在 thinking
        setWorkflowStatus('idle');
        setWorkflowSteps([]);
        seenWorkflowToolEvents.current.clear();
        setAgentMetadata(response);
        setMessages(prev => [...prev, {
          id: 'rest-' + requestId,
          text: response.text,
          userName: agentName,
          timestamp: new Date().toISOString(),
          type: 'agent'
        }]);
      } catch (err) {
        resolve();
        // REST fallback 失败：同步清理工作流面板，避免卡在 thinking
        setWorkflowStatus('idle');
        setWorkflowSteps([]);
        seenWorkflowToolEvents.current.clear();
        const message = t.failedToRouteNeuralMesh || "无法通过神经网格路由。";
        setMessages(prev => [...prev, {
          id: `err-${Date.now()}-${crypto.randomUUID()}`,
          text: `${t.requestFailed || '请求失败'}\n\n${message}`,
          userName: agentName,
          timestamp: new Date().toISOString(),
          type: 'agent',
          source: 'error',
        }]);
        toast.error(message);
      }
    }, 5000) : null;
  };

  // When prefillMessage comes from notification center, show it as a 小枫 message
  const sentRef = useRef<string>('');
  useEffect(() => {
    if (prefillMessage && prefillMessage !== sentRef.current) {
      sentRef.current = prefillMessage;
      setMessages(prev => {
        if (prev.some(m => m.text === prefillMessage && m.type === 'agent')) return prev;
        return [...prev, {
          id: `proactive-${Date.now()}`,
          text: prefillMessage,
          userName: agentName,
          timestamp: new Date().toISOString(),
          type: 'agent',
          source: 'proactive',
        }];
      });
      onPrefillConsumed?.();
    }
  // v3.12.2: 加 agentName 依赖，确保切换 agent 后用对名字
  }, [prefillMessage, onPrefillConsumed, agentName]);

  // Keep sendTextRef pointed at the latest sendText (recreated each render).
  useEffect(() => {
    sendTextRef.current = sendText;
  }, [sendText]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    sendText(draftTextRef.current.trim(), pendingAttachments);
  };

  const toggleListening = () => {
    if (!recognition.current) {
      toast.error(t.speechNotSupported || "当前浏览器不支持语音识别。");
      return;
    }

    if (isListening) {
      recognition.current.stop();
    } else {
      stop(); // Stop TTS if speaking
      recognition.current.start();
      setIsListening(true);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadExport = useCallback(async (format: 'md' | 'json' | 'pdf') => {
    const title = agentNameRef.current + '_' + new Date().toISOString().slice(0,10);
    if (format === 'md') {
      const md = messages.map(m => `**${m.userName || m.type}**: ${getDisplayText(m)}`).join('\n\n---\n\n');
      downloadBlob(new Blob([md], { type: 'text/markdown' }), title + '.md');
    } else if (format === 'json') {
      const json = JSON.stringify(messages.map(m => ({ type: m.type, user: m.userName, text: getDisplayText(m), time: m.timestamp })), null, 2);
      downloadBlob(new Blob([json], { type: 'application/json' }), title + '.json');
    } else if (format === 'pdf') {
      try {
        var pdfDoc = await PDFDocument.create();
        var font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        var page = pdfDoc.addPage([595, 842]);
        var y = 800;
        page.drawText(title, { x: 50, y: y, size: 18, font: font, color: rgb(0,0,0) });
        y -= 30;
        messages.forEach(function(m) {
          var sender = (m.userName || m.type || '?') + ':';
          var text = getDisplayText(m).substring(0, 200);
          page.drawText(sender, { x: 50, y: y, size: 10, font: font, color: rgb(0.3,0.3,0.3) });
          y -= 14;
          page.drawText(text, { x: 60, y: y, size: 9, font: font, color: rgb(0,0,0) });
          y -= 20;
          if (y < 60) { page = pdfDoc.addPage([595, 842]); y = 800; }
        });
        var pdfBytes = await pdfDoc.save();
        downloadBlob(new Blob([pdfBytes], { type: 'application/pdf' }), title + '.pdf');
      } catch (e) { console.warn('PDF export failed:', e); }
    }
  }, [messages]);

  var downloadBlob = function(blob, filename) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = filename;
    a.click(); URL.revokeObjectURL(url);
  };

  const uploadChatAttachments = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsOptimizing(true);
    setOptimizationProgress(30);

    const fileList = Array.from(files);
    const formData = new FormData();
    fileList.forEach(f => formData.append('files', f));

    try {
      formData.append('domain', activeDomain);
      if (activeDomain === 'work' && activeOrgId) formData.append('orgId', activeOrgId);

      const res = await apiFetch('/api/files/upload', { method: 'POST', body: formData, credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const attachments: ChatAttachment[] = (d.files || []).map((f: any) => {
          const fileName = f.name || f.displayName || f.id || '附件';
          const mimeType = f.mimeType || '';
          const kind = f.kind === 'image' || isImageFileName(fileName, mimeType) ? 'image' : 'file';
          return {
            id: `att-${Date.now()}-${crypto.randomUUID()}`,
            fileName,
            path: f.path,
            content: f.content || null,
            preview: f.preview || null,
            mimeType,
            size: f.rawSize || f.size || 0,
            kind,
            downloadUrl: f.id ? scopedFileUrl(`/api/files/download/${encodeURIComponent(f.id)}?inline=1`) : undefined,
          };
        });
        setPendingAttachments(prev => [...prev, ...attachments]);
        setOptimizationProgress(100);
        setTimeout(() => { setIsOptimizing(false); setOptimizationProgress(0); }, 500);
        if (attachments.length > 0) toast.success(ui('已添加到本条消息', 'Attached to this message'));
      } else {
        setIsOptimizing(false);
        setOptimizationProgress(0);
        try {
          const err = await res.json();
          toast.error(err.error || (t.uploadFailed || '上传失败'));
        } catch {
          toast.error(t.uploadFailed || '上传失败');
        }
      }
    } catch {
      setIsOptimizing(false);
      setOptimizationProgress(0);
      toast.error(t.chatConnError || '上传时连接错误');
    }
  };

  const removePendingAttachment = (id: string) => {
    setPendingAttachments(prev => prev.filter(item => item.id !== id));
  };

  const loadEarlierMessages = useCallback(async () => {
    if (!activeConversationId) return;
    const nextOffset = earlierOffset + CHAT_PAGE_SIZE;
    try {
      const res = await apiFetch(scopedConversationUrl('/api/conversations/' + activeConversationId + '/messages?limit=' + CHAT_PAGE_SIZE + '&offset=' + nextOffset));
      const data = await res.json();
      const incoming = normalizePersistedMessages(data.messages || []);
      if (incoming.length > 0) {
        const el = scrollRef.current;
        const prevHeight = el ? el.scrollHeight : 0;
        setMessages(prev => [...incoming, ...prev]);
        setEarlierOffset(nextOffset);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      }
    } catch (e) { /* ignore */ }
  }, [activeConversationId, earlierOffset, scopedConversationUrl]);

  if (isFounder) {
    return <FoundersSanctuary t={t} user={user} onBack={onClose} />;
  }

  const workflowHasExecution = workflowSteps.some(step =>
    step.type === 'background' ||
    step.type === 'confirmation' ||
    step.type === 'tool_start' ||
    step.type === 'tool_result' ||
    step.type === 'error'
  );
  const workflowPanelVisible =
    isOpen &&
    (workflowStatus !== 'idle' || workflowSteps.length > 0 || workflowHasExecution || backgroundWorkflowTasks.length > 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.99 }}
          transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
          className={embedded ? 'relative h-full flex-1 flex flex-col' : 'fixed inset-0 z-[210] flex flex-col'}
          style={{
            background: 'radial-gradient(ellipse at 50% 30%, #0a0f1e 0%, #060810 40%, #020205 100%)',
            willChange: 'opacity, transform',
          }}
        >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple
        accept={CHAT_ATTACHMENT_ACCEPT}
        onChange={(e) => { uploadChatAttachments(e.target.files); e.target.value = ''; }}
      />
      <WorkflowPanel
        visible={workflowPanelVisible}
        agentStatus={workflowStatus}
        steps={workflowSteps}
        t={t}
        placement="corner"
        backgroundTasks={backgroundWorkflowTasks}
        onCancelBackgroundTask={cancelBackgroundWorkflowTask}
      />
      <AnimatePresence>
        {showWeChatSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[230] flex items-start justify-center bg-black/60 px-4 pt-20 backdrop-blur-sm"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setShowWeChatSettings(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[#080b12]/95 shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                <div>
                  <h3 className="text-sm font-bold text-white">{ui('个人微信连接', 'Personal WeChat')}</h3>
                  <p className="mt-1 text-xs text-white/40">{ui('扫码后，小枫 可以通过你的个人微信接收消息。', 'After scanning, 小枫 can receive messages through your personal WeChat.')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowWeChatSettings(false)}
                  className="rounded-full p-1.5 text-white/35 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label={ui('关闭', 'Close')}
                >
                  <XCircle size={18} />
                </button>
              </div>
              <div className="p-5">
                <WeChatSettings t={t} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    <div className="flex-1 max-w-[90rem] mx-auto w-full space-y-4 md:space-y-8 pb-32 md:pb-0 overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-0 pt-6 flex-shrink-0">
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center bg-black/40 backdrop-blur-xl border border-white/[0.08] rounded-2xl text-white/40 hover:text-white hover:border-white/20 transition-all"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMsgSelectMode(!msgSelectMode)} className={"w-9 h-9 flex items-center justify-center rounded-xl border transition-all " + (msgSelectMode ? 'bg-celestial-saturn/20 border-celestial-saturn/40 text-celestial-saturn' : 'bg-black/40 border-white/[0.08] text-white/40 hover:text-white hover:border-white/20')} title="多选消息" aria-label="多选消息"
            >
              <Square size={16} />
            </button>
            <button
            onClick={() => setAutoSpeakReply(!autoSpeakReply)}
            className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all ${autoSpeakReply ? 'bg-celestial-saturn/20 border-celestial-saturn/40 text-celestial-saturn' : 'bg-black/40 border-white/[0.08] text-white/40 hover:text-white hover:border-white/20'}`}
            title={autoSpeakReply ? '自动语音回复：开（点击关闭）' : '自动语音回复：关（点击开启）'}
            aria-label="自动语音回复"
          >
            {autoSpeakReply ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVoicePicker(!showVoicePicker)}
              className="text-xs font-black uppercase tracking-widest text-white/40 flex items-center gap-2 hover:text-celestial-saturn transition-colors"
            >
              {voices.find(v => v.voiceId === selectedVoiceId) ? voiceDisplayName(voices.find(v => v.voiceId === selectedVoiceId)!) : (t.selectVoice || '选择语音')}
              <ChevronDown size={12} />
            </Button>
            
            <AnimatePresence>
              {showVoicePicker && (
                <motion.div
                  ref={voicePickerRef}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 mt-2 w-48 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-2 z-50 shadow-2xl max-h-64 overflow-y-auto custom-scrollbar"
                >
                  {voices.map(v => (
                    <button
                      key={v.voiceId}
                      data-voice-id={v.voiceId}
                      onClick={handleVoiceSelect}
                      className={`w-full text-left p-2 rounded-xl text-xs font-bold uppercase transition-all ${
                        selectedVoiceId === v.voiceId ? 'bg-celestial-saturn text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {voiceDisplayName(v)}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <VoiceCallButton
            callState={callState}
            audioLevel={audioLevel}
            onStart={() => startCall(selectedVoiceId, 'xiaofeng', agentId, { domain: activeDomain, orgId: activeOrgId })}
            onEnd={endCall}
            hasVoice={voices.length > 0}
          />
          {activeDomain === 'personal' && (
            <button
              type="button"
              onClick={() => setShowWeChatSettings(true)}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-emerald-400/20 bg-emerald-400/10 text-emerald-200 transition-all hover:border-emerald-300/35 hover:bg-emerald-400/15 md:h-10 md:w-10"
              title={ui('个人微信连接', 'Personal WeChat')}
              aria-label={ui('打开个人微信连接', 'Open personal WeChat connection')}
            >
              <MessageCircle className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={requestMeetingMode}
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/10 text-cyan-200 transition-all hover:border-cyan-300/35 hover:bg-cyan-400/15 md:h-10 md:w-10"
            title={ui('会议模式', 'Meeting mode')}
            aria-label={ui('打开会议模式', 'Open meeting mode')}
          >
            <FileText className="h-4 w-4 md:h-5 md:w-5" />
          </button>
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-celestial-saturn/20 flex items-center justify-center text-celestial-saturn border border-celestial-saturn/20">
            <Ghost className="w-4 h-4 md:w-5 md:h-5" />
          </div>
          <div className="text-right sm:text-left">
            <h2 className="text-base md:text-xl font-bold tracking-tight truncate max-w-[120px] sm:max-w-none flex items-center gap-2">
              {titleEditing ? (
                <input
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onBlur={commitTitleEdit}
                  onKeyDown={e => { if (e.key === 'Enter') commitTitleEdit(); if (e.key === 'Escape') { setTitleEditing(false); setTitleDraft(agentName); } }}
                  className="bg-black/40 border border-celestial-saturn/40 rounded-xl p-1.5 text-base md:text-xl font-bold text-white outline-none max-w-[200px]"
                  autoFocus
                />
              ) : (
                <span onClick={() => { setTitleEditing(true); setTitleDraft(agentName); }} className="cursor-pointer hover:text-celestial-saturn transition-colors" title="点击编辑名称">
                  {agentName}
                </span>
              )}
              <span
                className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  isWorkChat
                    ? 'border-blue-500/30 bg-blue-500/15 text-blue-300'
                    : 'border-white/10 bg-white/5 text-white/45'
                }`}
                title={activeDomainDetail}
              >
                {isWorkChat ? <Briefcase size={11} /> : <User size={11} />}
                {activeDomainLabel}
              </span>
              {activeConversationId && (
                <span className="relative inline-flex shrink-0 items-center">
                  <button
                    onClick={() => setShowProjMenu((v) => !v)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-all ${
                      currentProject
                        ? 'border-celestial-saturn/30 bg-celestial-saturn/10 text-celestial-saturn'
                        : 'border-white/10 bg-white/5 text-white/40 hover:text-white/70'
                    }`}
                    title={isZh ? '所属项目' : 'Project'}
                  >
                    {currentProject ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: currentProject.color }} />
                        {currentProject.name}
                      </>
                    ) : (
                      <>
                        <TagIcon size={11} /> {isZh ? '未分组' : 'No project'}
                      </>
                    )}
                  </button>
                  <AnimatePresence>
                    {showProjMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute left-0 top-7 z-40 w-48 max-h-60 overflow-y-auto custom-scrollbar bg-[#0b1020] border border-white/10 rounded-xl shadow-xl p-1"
                      >
                        <button
                          onClick={() => handleAssignConvProject(null)}
                          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-white/50 hover:bg-white/[0.06]"
                        >
                          <FolderOpen size={12} /> {isZh ? '无项目' : 'No project'}
                          {!convProjectId && <Check size={12} className="ml-auto text-celestial-saturn" />}
                        </button>
                        {projProjects.map((p) => (
                          <button
                            key={p.id}
                            data-project-id={p.id}
                            onClick={handleProjectSelect}
                            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06]"
                          >
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                            <span className="truncate">{p.name}</span>
                            {convProjectId === p.id && <Check size={12} className="ml-auto text-celestial-saturn" />}
                          </button>
                        ))}
                        {projProjects.length === 0 && (
                          <div className="px-2.5 py-2 text-[10px] text-white/30">{isZh ? '去项目空间新建' : 'Create in Project Space'}</div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </span>
              )}
            </h2>
            <p className="text-xs md:text-xs uppercase tracking-widest text-white/40 font-bold">{agentCategory}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 flex-1 min-h-0">

        {/* Chat Panel */}
        <div className="flex-1 flex flex-col glass rounded-[2.5rem] md:rounded-[3rem] border-white/10 overflow-hidden shadow-2xl min-w-0">
          <div className="p-4 md:p-6 border-b border-white/5 flex items-center justify-between bg-white/5">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-celestial-nebula animate-ping' : 'bg-celestial-saturn animate-pulse'}`} />
              <span className="text-xs md:text-xs font-bold uppercase tracking-widest text-white/60">
                {isZh ? '神经连接' : 'Neural Link'}
              </span>
              {isSpeaking && (
                <div className="flex items-center gap-3 ml-2 md:ml-4 scale-75 md:scale-100 origin-left">
                  <div className="flex items-end gap-1 h-4">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ height: [4, 16, 4] }}
                        transition={{ 
                          duration: 0.5 + audioWaveRandoms.current[i], 
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="w-1 bg-celestial-nebula rounded-full"
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={isPaused ? resume : pause}
                      className="h-6 px-2 text-xs bg-white/10 text-white hover:bg-white/20 rounded-full border border-white/10 flex items-center gap-1"
                    >
                      {isPaused ? <Play size={10} /> : <Pause size={10} />}
                    </Button>
                    <Button 
                      onClick={stop}
                      className="h-6 px-2 text-xs bg-red-500/20 text-red-500 hover:bg-red-500/40 rounded-full border border-red-500/20 flex items-center gap-1"
                    >
                      <Square size={10} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setMessages([]);
                  setActiveConversationId(null);
                  initialLoadDoneRef.current = true;
                  lastLoadedConvIdRef.current = null;
                  onConversationChange?.(null);
                  socket.emit("chat:new_conversation", { agentId, domain: undefined, orgId: null });
                }}
                className="h-7 px-3 text-xs bg-white/10 text-white hover:bg-white/20 rounded-full border border-white/10 flex items-center gap-1"
                title="新建对话"
              >
                <Plus size={14} />
                <span className="hidden sm:inline">新建</span>
              </Button>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={isZh ? '搜索历史对话...' : 'Search saved history...'}
                  className="h-7 w-40 px-3 py-0 text-xs bg-white/5 border border-white/10 rounded-full text-white/60 placeholder:text-white/20 outline-none focus:border-white/20 focus:bg-white/[0.07] transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchResults([]); setSearchError(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                  >
                    <XCircle size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 md:p-8 space-y-4 md:space-y-6 custom-scrollbar"
          >
            {messages.length === 0 && !searchQuery.trim() && (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-10 px-4">
                {/* Hero illustration — chat bubble with cursor */}
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="text-celestial-saturn/60">
                      <path d="M6 8h28a3 3 0 0 1 3 3v16a3 3 0 0 1-3 3H14l-6 5V11a3 3 0 0 1 3-3z" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <circle cx="14" cy="18" r="1.5" fill="currentColor"/>
                      <circle cx="20" cy="18" r="1.5" fill="currentColor"/>
                      <circle cx="26" cy="18" r="1.5" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-celestial-saturn/20 border border-celestial-saturn/30 flex items-center justify-center">
                    <div className="w-1.5 h-3 rounded-full bg-celestial-saturn animate-pulse" style={{ animationDuration: '1.2s' }} />
                  </div>
                </div>

                {/* Welcome text */}
                <div className="space-y-2 max-w-sm">
                  <p className="text-xl font-bold text-white/80">
                    {t.awakePrompt || '和小枫开始对话'}
                  </p>
                  <p className="text-sm text-white/40 leading-relaxed">
                    {t.awakePromptSub || '我能帮你搜索记忆、执行任务、管理知识图谱。直接在下方输入你想做的事吧。'}
                  </p>
                </div>

                {/* Primary CTA — visible new conversation hint */}
                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-celestial-saturn/5 border border-celestial-saturn/15">
                  <span className="text-xs text-white/50">💬</span>
                  <span className="text-sm text-white/55">
                    {isZh ? '点击下方建议，或直接输入你的问题' : 'Tap a suggestion below, or type your question'}
                  </span>
                </div>

                {/* Suggestion cards */}
                {visibleSuggestions.length > 0 && (
                  <div className="space-y-3 max-w-md w-full">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/45 font-bold">
                      <Sparkles size={11} />
                      {t.tryThese || '试试这些'}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {visibleSuggestions.map(s => (
                        <button
                          key={s.id}
                          data-prompt={s.prompt}
                          onClick={handleSuggestionSelect}
                          className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/10 text-sm text-white/55 hover:text-celestial-saturn hover:border-celestial-saturn/25 hover:bg-celestial-saturn/[0.06] transition-all text-left group"
                        >
                          <span>{s.label}</span>
                          <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-celestial-saturn" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Socket 断线提示条 */}
            {socketStatus !== 'connected' && (
              <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[600] flex items-center gap-2 rounded-xl bg-red-500/20 border border-red-500/40 px-4 py-2.5 shadow-xl">
                <AlertCircle size={16} className="text-red-400" />
                <span className="text-xs font-bold text-white/80">连接断开中...重试中</span>
                <button onClick={() => socketService.getSocket()?.connect()} className="ml-2 rounded-lg bg-white/10 px-2 py-1 text-[10px] font-bold hover:bg-white/20">立即重试</button>
              </div>
            )}
            {searchQuery.trim() && (
              <div className={`text-[10px] font-mono uppercase tracking-wider text-center ${searchError ? 'text-red-300/70' : 'text-white/30'}`}>
                {isSearchingHistory
                  ? ui('正在搜索历史对话...', 'Searching saved history...')
                  : searchError
                    ? searchError
                    : ui(`${searchDisplayMessages.length} 条匹配`, `${searchDisplayMessages.length} matches`)}
              </div>
            )}
            {searchQuery.trim() && !isSearchingHistory && !searchError && searchDisplayMessages.length === 0 && (
              <div className="h-full flex items-center justify-center text-center text-xs text-white/30">
                {ui('没有匹配的历史对话记录。', 'No saved conversation records match.')}
              </div>
            )}
            {hiddenMessageCount > 0 && (
              <div className="text-center text-[10px] font-mono uppercase tracking-wider text-white/25 space-y-1">
                <div>{ui(`已折叠 ${hiddenMessageCount} 条较早消息`, `${hiddenMessageCount} older messages hidden`)}</div>
                <button
                  type="button"
                  onClick={loadEarlierMessages}
                  className="mx-auto block rounded-full border border-white/15 px-3 py-1 text-white/60 hover:bg-white/10"
                >
                  {ui('加载更早的消息', 'Load earlier messages')}
                </button>
              </div>
            )}

            {/* P3 Step 4: Intent Visualization UI - show classification metadata */}
            {intentData && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500/5 to-purple-500/5 border border-cyan-500/20 text-xs text-white/60"
                title={`意图：${intentData.intent} | 置信度：${Math.round(intentData.confidence * 100)}% | 模型：${intentData.suggestedModel}`}
              >
                <Sparkles size={14} className="text-cyan-400" />
                <span className="font-bold text-cyan-300">{intentData.intent}</span>
                <span className="text-white/30">•</span>
                <span>{(intentData.confidence * 100).toFixed(0)}%</span>
                <span className="text-white/30">•</span>
                <span className="font-mono text-purple-300">{intentData.suggestedModel}</span>
                <span className="text-white/30">•</span>
                <span>{intentData.metadata.processingTimeMs}ms</span>
              </motion.div>
            )}

            <AnimatePresence initial={false}>
              {displayMessages.map((msg) => (
                msg.type === 'file_context' ? null /* invisible context */ : msg.type === 'tool' ? (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-start"
                  >
                    <div className={`relative max-w-[85%] p-4 rounded-2xl text-xs ${
                      msg.toolStatus === 'error'
                        ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                        : msg.toolStatus === 'done'
                          ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300'
                        : 'bg-amber-500/5 border border-amber-500/20 text-amber-400'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        {msg.toolStatus === 'error' ? (
                          <XCircle size={14} />
                        ) : msg.toolStatus === 'done' ? (
                          <CheckCircle2 size={14} />
                        ) : (
                          <Loader2 size={14} className="animate-spin" />
                        )}
                        <span className="font-bold uppercase tracking-widest text-xs">{msg.toolName}</span>
                      </div>
                      {msg.toolArgs && (
                        <div className="text-xs opacity-50 truncate max-w-[200px]">
                          {JSON.stringify(msg.toolArgs).slice(0, 80)}
                        </div>
                      )}
                      {msg.toolResult && (
                        <div className="text-xs text-green-400/70 mt-1 truncate max-w-[250px]">{msg.toolResult.slice(0, 150)}</div>
                      )}
                      {msg.toolError && (
                        <>
                          <div className="text-xs text-red-400/80 mt-1 break-words">{msg.toolError}</div>
                          <div className="text-xs text-red-300/50 mt-1">{toolFailureHint}</div>
                        </>
                      )}
                    </div>
                    {renderGeneratedFiles(extractGeneratedFiles([msg.toolResult, msg.text].filter(Boolean).join('\n')), 'start')}
                  </motion.div>
                ) : (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-2.5 ${msg.type === 'agent' ? 'flex-row' : 'flex-row-reverse'} ${msgSelectMode ? 'cursor-pointer' : ''}`}
                  onClick={msgSelectMode ? function(e) { e.stopPropagation(); toggleMsgSelect(msg.id); } : undefined}
                >
                  <MessageAvatar isAgent={msg.type === 'agent'} userName={msg.userName} personalityId={personalityId} />
                  <div className={`flex flex-col min-w-0 flex-1 ${msg.type === 'agent' ? 'items-start' : 'items-end'}`}>
                  {msgSelectMode && (
                    <div className={'mb-1 flex items-center gap-2 ' + (msg.type === 'agent' ? 'self-start' : 'self-end')}>
                      <div className={'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ' + (selectedMsgIds.has(msg.id) ? 'bg-celestial-saturn border-celestial-saturn text-black' : 'border-white/20 text-transparent')}>
                        {selectedMsgIds.has(msg.id) ? <Check size={12} /> : null}
                      </div>
                    </div>
                  )}
                  {/* Image / file previews */}
                  {(() => {
                    const messageText = getDisplayText(msg);
                    let imageUrls: string[] = [];
                    try {
                      const parsed = JSON.parse(messageText || '');
                      if (parsed.images && Array.isArray(parsed.images)) imageUrls = parsed.images;
                      if (parsed.image_base64) imageUrls = [`data:image/png;base64,${parsed.image_base64}`];
                    } catch {}
                    const generatedFiles = extractGeneratedFiles(messageText);
                    if (imageUrls.length === 0 && generatedFiles.length === 0) return null;
                    return (
                      <div className="max-w-[85%] mb-1 space-y-2">
                        {imageUrls.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {imageUrls.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                className="block w-36 h-36 rounded-2xl overflow-hidden border-2 border-white/10 hover:border-celestial-saturn/60 transition-all shadow-lg">
                                <img src={url} alt={`Generated ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                              </a>
                            ))}
                          </div>
                        )}
                        {renderGeneratedFiles(generatedFiles, msg.type === 'agent' ? 'start' : 'end')}
                      </div>
                    );
                  })()}

                  {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                    <div className={`max-w-[85%] mb-2 flex flex-wrap gap-2 ${msg.type === 'agent' ? '' : 'justify-end'}`}>
                      {msg.attachments.map((item: ChatAttachment) => {
                        const card = (
                          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/25 px-3 py-2 text-xs text-white/65">
                            {item.kind === 'image' && item.downloadUrl ? (
                              <img src={item.downloadUrl} alt={item.fileName} className="h-8 w-8 rounded-lg object-cover" loading="lazy" />
                            ) : item.kind === 'image' ? (
                              <ImageIcon size={16} className="text-celestial-saturn" />
                            ) : (
                              <FileText size={16} className="text-white/45" />
                            )}
                            <span className="max-w-[180px] truncate">{item.fileName}</span>
                          </div>
                        );
                        return item.downloadUrl ? (
                          <a key={item.id} href={item.downloadUrl} target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-80">
                            {card}
                          </a>
                        ) : (
                          <div key={item.id}>{card}</div>
                        );
                      })}
                    </div>
                  )}

                  {editingMsgId === msg.id && msg.type === 'user' ? (
                  <div className="max-w-[85%] w-full mb-1">
                    <textarea
                      defaultValue={getDisplayText(msg)}
                      className="w-full bg-black/40 border border-celestial-saturn/40 rounded-2xl p-4 text-sm text-white/90 outline-none resize-none focus:border-celestial-saturn min-h-[80px]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleEditSubmit(msg.id, (e.target as HTMLTextAreaElement).value);
                        }
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                      autoFocus
                    />
                    <div className="flex gap-2 mt-1.5 justify-end">
                      <button onClick={handleEditCancel} className="px-3 py-1 text-[11px] rounded-lg bg-white/5 text-white/50 hover:bg-white/10 transition-colors">取消</button>
                      <button onClick={(e) => { const ta = (e.target as HTMLElement).parentElement?.parentElement?.querySelector('textarea'); if (ta) handleEditSubmit(msg.id, ta.value); }} className="px-3 py-1 text-[11px] rounded-lg bg-celestial-saturn text-black font-bold hover:bg-celestial-saturn/80 transition-colors">发送</button>
                    </div>
                  </div>
                ) : (
                <div className={`relative group text-sm leading-relaxed ${
                    msg.type === 'agent'
                      ? 'max-w-[92%] md:max-w-[84%] rounded-[1.5rem] rounded-tl-none border border-white/10 bg-white/[0.055] p-5 text-white/85 shadow-xl shadow-black/10 md:p-6'
                      : 'max-w-[85%] rounded-3xl rounded-tr-none border border-white/10 bg-white/5 p-5 text-white/80'
                  }`}>
                    {/* Mermaid diagram rendering */}
                    {(() => {
                      const txt = getDisplayText(msg);
                      const mermaidMatch = txt.match(/```mermaid\n?([\s\S]*?)```/);
                      if (mermaidMatch && msg.type === 'agent') {
                        const diagramId = 'mermaid-' + msg.id;
                        const cleanDiagram = mermaidMatch[1].trim();
                        if (!mermaidRenderedIds.current.has(diagramId)) {
                          mermaidRenderedIds.current.add(diagramId);
                          const timer = setTimeout(async () => {
                            try {
                              const el = document.getElementById(diagramId);
                              if (el && document.body.contains(el)) {
                                const { svg } = await mermaid.render(diagramId + '-svg', cleanDiagram);
                                if (document.body.contains(el)) { el.innerHTML = svg; }
                              }
                            } catch (e) { console.warn('Mermaid error:', e); }
                          }, 100);
                          mermaidTimers.current.add(timer);
                        }
                        // Strip mermaid block for text display
                        const textWithoutMermaid = txt.replace(/```mermaid[\s\S]*?```/g, '').trim();
                        return (
                          <>
                            <div id={diagramId} className="my-4 p-4 bg-white/[0.03] rounded-xl border border-white/10 overflow-x-auto" />
                            {textWithoutMermaid ? null : <div className="text-xs text-white/30 italic">Mermaid 图表</div>}
                          </>
                        );
                      }
                      return null;
                    })()}

                    {/* Enhanced Markdown Rendering with Syntax Highlighting */}
                    <div className={`markdown-body chat-message-markdown ${msg.type === 'agent' ? 'chat-message-markdown-agent' : 'chat-message-markdown-user'}`}>
                      <Markdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeHighlight, [rehypeKatex, { output: 'html' }] ]}
                        components={{
                          code({ node, className, children, ...props }: any) {
                            const match = /language-(\w+)/.exec(className || '');
                            const codeText = String(children).replace(/\n$/, '');
                            const isInline = !match && !className;
                            if (isInline) {
                              return <code className={className} {...props}>{children}</code>;
                            }
                            const lang = match ? match[1] : '';
                            const isThisCopied = codeCopied === codeText.slice(0, 20);
                            
                            return (
                              <EnhancedCodeBlock
                                lang={lang}
                                text={codeText}
                                onCopy={() => handleCodeCopy(codeText)}
                              />
                            );
                          },
                          blockquote: ({ children }) => (
                            <blockquote className="pl-4 border-l-4 border-celestial-saturn/40 bg-white/[0.03] py-2 px-4 my-4 rounded-r-xl">
                              {children}
                            </blockquote>
                          ),
                          table: ({ children }) => (
                            <div className="overflow-x-auto mb-4 rounded-xl border border-white/10">
                              <table className="w-full">{children}</table>
                            </div>
                          ),
                          th: ({ children }) => (
                            <th className="bg-white/[0.06] text-white/75 font-bold py-2 px-3 text-left text-xs">
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td className="py-2 px-3 text-white/60 text-sm border-t border-white/5">
                              {children}
                            </td>
                          )
                        }}>
                        {getDisplayText(msg)}
                      </Markdown>
                    </div>

                    {/* 401 API key 错误引导 — 仅对真正的错误消息(source==='error')生效，避免普通消息提到 401 时误报（如主动扫描播报） */}
                    {msg.source === 'error' && /401|incorrect api key|invalid api key|invalidapikey|api.?key.*(invalid|expired|incorrect)/i.test(getDisplayText(msg)) && (
                      <div className="mt-2 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-300">
                        {isZh ? 'API key 可能无效或已过期，请前往设置 → API 密钥更新百炼 (DashScope) key' : 'API key may be invalid or expired — update it in Settings → API Keys (DashScope)'}
                      </div>
                    )}
                    {getDisplayText(msg) && (
                      <button
                        data-copy-text={getDisplayText(msg)}
                        data-msg-id={msg.id}
                        onClick={handleMessageCopy}
                        className={`absolute top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-white/10 ${
                          msg.type === 'agent' ? 'right-2' : 'left-2'
                        }`}
                      >
                        {copiedId === msg.id ? (
                          <Check size={12} className="text-green-400" />
                        ) : (
                          <Copy size={12} className="text-white/55 hover:text-white/70" />
                        )}
                      </button>
                    )}
                    {/* Action buttons */}
                    <div className={
                      msg.type === 'agent'
                        ? 'absolute -bottom-7 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5'
                        : 'absolute -bottom-7 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5'
                    }>
                      {msg.type === 'user' && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditMessage(msg.id); }}
                          className="p-1 rounded-md hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors"
                          title="编辑"
                        >
                          <Pencil size={11} />
                        </button>
                      )}
                      {msg.type === 'agent' && (
                        <>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFeedback(msg.id, 'like'); }}
                            className={feedbackMap[msg.id] === 'like' ? 'p-1 rounded-md text-green-400 bg-green-400/10' : 'p-1 rounded-md hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors'}
                            title="有帮助"
                          >
                            <ThumbsUp size={11} />
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFeedback(msg.id, 'dislike'); }}
                            className={feedbackMap[msg.id] === 'dislike' ? 'p-1 rounded-md text-red-400 bg-red-400/10' : 'p-1 rounded-md hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors'}
                            title="没帮助"
                          >
                            <ThumbsDown size={11} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRegenerate(); }}
                        className="p-1 rounded-md hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors"
                        title="重新生成"
                      >
                        <RefreshCw size={11} />
                      </button>
                    </div>
                  </div>
                  )}
                  <span className="text-[12px] uppercase tracking-widest opacity-30 mt-2 px-3">
                    {msg.userName} - {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  </div>
                </motion.div>
              )))}
            </AnimatePresence>
            {isTyping && (
              <div className="flex flex-col gap-3">
                <div className="flex gap-2 items-center text-celestial-saturn/40 text-xs font-bold uppercase tracking-widest">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader2 size={14} />
                  </motion.div>
                  {t.neuralProcessing || '神经处理中...'}
                </div>
                <div className="flex gap-1">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ 
                        scale: [1, 1.5, 1],
                        opacity: [0.3, 1, 0.3]
                      }}
                      transition={{ 
                        duration: 1, 
                        repeat: Infinity, 
                        delay: i * 0.2 
                      }}
                      className="w-1.5 h-1.5 rounded-full bg-celestial-saturn"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 bg-white/5 border-t border-white/5">
            {isOptimizing && (
              <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-white/5">
                <motion.div
                  className="h-full bg-celestial-saturn"
                  initial={{ width: 0 }}
                  animate={{ width: `${optimizationProgress}%` }}
                />
              </div>
            )}
            <ChatInputToolbar
              onSend={(text) => sendText(text, pendingAttachments)}
              isTyping={isTyping}
              isListening={isListening}
              onToggleListening={toggleListening}
              onAbort={() => { socket?.emit('agent:abort_chat'); streamingMsgId.current = null; setIsTyping(false); }}
              disabled={isOptimizing}
              pendingAttachments={pendingAttachments}
              onRemoveAttachment={removePendingAttachment}
              onOpenFilePicker={() => fileInputRef.current?.click()}
              installedSkills={installedSkillNames}
              mcpConnectors={mcpConnectorNames}
              draftText={draftText}
              onDraftChange={updateDraftText}
              strategy={chatStrategy}
              onStrategyChange={setChatStrategy}
              teamMode={teamMode}
              onTeamModeChange={function(m) {
                setTeamMode(m);
                try { localStorage.setItem('xf_team_mode', m); } catch {}
              }}
              t={t}
              ui={ui}
              messageInputRef={messageInputRef}
              fileInputRef={fileInputRef}
              conversationId={activeConversationId || undefined}
              isNewConversation={messages.length === 0 && !searchQuery.trim()}
              quoteMessages={messages}
              onQuote={handleQuoteMessage}
              onSkillSelect={handleSkillMention}
              onConnectorSelect={handleConnectorMention}
              onEnhancePrompt={async function(text) {
                try {
                  // v3.6.65 fix: 服务端 chat_routes.ts 只读 req.body.prompt 或 message，不读 text
                  // 之前传 text: ... 导致服务端拿到 undefined，LLM 收到空 user 消息，personality prompt 主导返回无关内容
                  const fullPrompt = '请帮我优化扩写以下提示词，使其更加详细、具体、结构化。直接返回优化后的版本，不要加解释：\n\n' + text;
                  var res = await apiFetch('/api/chat', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                      prompt: fullPrompt,         // ← 服务端读的字段
                      message: fullPrompt,        // ← 备用
                      text: fullPrompt,           // ← 保留兼容
                      personalityId,
                      source: 'prompt_enhance'
                    })
                  });
                  if (res.ok) {
                    var data = await res.json();
                    return data.text || data.response || data.content || text;
                  }
                } catch {}
                return text;
              }}
              slashCommands={SLASH_COMMANDS}
              onSlashCommand={(cmd: SlashCommand) => {
                if (cmd.action === 'clear') setMessages([]);
                else if (cmd.action === 'export') {
                  downloadExport('md');
                } else if (cmd.action === 'exportJson') {
                  downloadExport('json');
                } else if (cmd.action === 'exportPdf') {
                  downloadExport('pdf');
                } else if (cmd.text) {
                  setDraftText(cmd.text);
                  messageInputRef.current?.focus();
                }
              }}
            />
          </div>
        </div>

        {/* Info Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1], delay: 0.15 }}
              className="w-96 flex-shrink-0 space-y-4 overflow-y-auto custom-scrollbar">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }}>
          <GlassPanelCard bordered>
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-widest text-white/40">{t.activeCapabilities || '当前能力'}</h4>
              {isElectron && (
                <div className="px-2 py-0.5 rounded-full bg-celestial-saturn/20 text-xs text-celestial-saturn font-black">NODE_NATIVE</div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(agentMetadata.capabilities || [t.neuralCore || '神经核心', t.webMesh || '网络网格']).map((cap, i) => (
                <div key={i} className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/5 text-xs text-white/60 font-bold flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-celestial-saturn" />
                  {cap}
                </div>
              ))}
            </div>
          </GlassPanelCard>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.36 }}>
          <GlassPanelCard>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/40">{t.agentStats || '智能体统计'}</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-white/60 flex items-center gap-2"><Cpu size={14}/> {t.logicEngine || '逻辑引擎'}</span>
                <span className="text-sm font-bold text-celestial-saturn">{engineVersion}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-white/60 flex items-center gap-2"><Zap size={14}/> {t.syncSpeed || '同步速度'}</span>
                <span className="text-sm font-bold text-celestial-mars">{syncSpeedMs > 0 ? `${syncSpeedMs}ms` : '—'}</span>
              </div>
            </div>
          </GlassPanelCard>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.44 }}>
          <GlassPanelCard>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/40">{t.neuralMeshStatus || '神经网络状态'}</h4>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-bold">{t.encryptedLinkActive || '加密链路已激活'}</span>
            </div>
            <p className="text-xs text-white/40 leading-relaxed">
              {t.agentSyncDesc || '你的智能体当前已与本地节点同步。所有交互都存储在你的私有神经云中。'}
            </p>
          </GlassPanelCard>
          </motion.div>

          {livePlan && (
            <TaskListCard plan={livePlan} />
          )}
            </motion.div>
      </div>
    </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
function TaskListCard({ plan }: { plan: any }) {
  const steps: any[] = plan?.steps || [];
  const done = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  const total = steps.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const statusMeta: Record<string, { label: string; color: string; Icon: any }> = {
    pending: { label: '待执行', color: 'text-white/40', Icon: Circle },
    in_progress: { label: '进行中', color: 'text-celestial-saturn', Icon: Loader },
    done: { label: '已完成', color: 'text-green-400', Icon: CheckCircle2 },
    skipped: { label: '已跳过', color: 'text-white/30', Icon: CircleDot },
  };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.5 }}>
      <GlassPanelCard bordered>
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 flex items-center gap-2">
            <ListTodo size={14} className="text-celestial-saturn" /> 当前任务
          </h4>
          <span className="text-xs font-black text-celestial-saturn">{pct}%</span>
        </div>
        <div className="text-sm font-bold text-white/80 leading-snug">{plan?.title || '智能体任务'}</div>
        {plan?.description && (
          <p className="text-xs text-white/40 leading-relaxed line-clamp-2">{plan.description}</p>
        )}
        <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-celestial-saturn transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar pr-1">
          {steps.map((s) => {
            const m = statusMeta[s.status] || statusMeta.pending;
            const MIcon = m.Icon;
            return (
              <div key={s.id} className="flex items-start gap-2.5">
                <MIcon size={15} className={`mt-0.5 flex-shrink-0 ${m.color} ${s.status === 'in_progress' ? 'animate-spin' : ''}`} />
                <div className="min-w-0">
                  <div className={`text-sm font-bold ${s.status === 'pending' ? 'text-white/45' : 'text-white/80'}`}>{s.title}</div>
                  {s.result && s.status === 'done' && (
                    <div className="text-xs text-white/35 line-clamp-1">{s.result}</div>
                  )}
                </div>
              </div>
            );
          })}
          {total === 0 && <div className="text-xs text-white/30">暂无步骤</div>}
        </div>
      </GlassPanelCard>
    </motion.div>
  );
}

export const AgentChatPage = memo(AgentChatPageImpl);
